/*
 * Copyright 2025 The Android Open Source Project
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import data.storage.SecureStorageFactory
import auth.UserIdManager
import i18n.AppLocale
import i18n.LocaleManager
import i18n.StringKey
import i18n.Strings
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.localStorage
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import ml.correctTransactionCategory
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement
import ui.AppTabType
import models.getDaysRemaining
import repository.createSavingsGoalsRepositoryWithAuth

/**
 * Web application navigation manager
 */
class WebNavigationManager {
    private var currentScreen: Screen = Screen.Login
    private var currentTab: AppTabType = AppTabType.TRANSACTIONS
    private val listeners = mutableListOf<(Screen) -> Unit>()
    private val tabListeners = mutableListOf<(AppTabType) -> Unit>()
    private var accessToken: String? = null
    private var selectedTransaction: Transaction? = null
    private val secureStorage = SecureStorageFactory.create()
    private var transactionRefreshNeeded: Boolean = false
    private var previousScreen: Screen? = null

    fun requestTransactionRefresh() {
        transactionRefreshNeeded = true
    }

    fun consumeTransactionRefresh(): Boolean {
        val needed = transactionRefreshNeeded
        transactionRefreshNeeded = false
        return needed
    }

    fun getCurrentScreen(): Screen = currentScreen

    fun getCurrentTab(): AppTabType = currentTab

    fun navigateTo(screen: Screen) {
        // Screen.Landing is no longer rendered inside the SPA — the marketing
        // landing lives at / as static HTML. Any in-SPA navigation that would
        // land on it (e.g. dashboard/goals/budget/charts logo clicks scattered
        // across the codebase) leaves the SPA entirely. This is the single
        // chokepoint so we don't have to track down every individual call site.
        if (screen is Screen.Landing) {
            kotlinx.browser.window.location.href = "/"
            return
        }
        // Guard against navigating to the same screen (prevents infinite loops)
        if (currentScreen == screen) {
            console.log("🚀 Already on screen $screen, skipping navigation")
            return
        }
        console.log("🚀 NavigationManager.navigateTo() called with screen: $screen")
        console.log("🚀 Previous screen was: $currentScreen")
        previousScreen = currentScreen
        currentScreen = screen
        // Keep currentTab in sync so switchToTab() guards work correctly
        // when navigating via navigateTo() instead of switchToTab()
        when (screen) {
            is Screen.SavingsGoals -> currentTab = AppTabType.GOALS
            is Screen.TransactionList -> currentTab = AppTabType.TRANSACTIONS
            else -> {}
        }
        console.log("🚀 Current screen updated to: $currentScreen")
        console.log("🚀 Notifying ${listeners.size} listeners...")
        notifyListeners()
        kotlinx.browser.window.scrollTo(0.0, 0.0)
        console.log("🚀 Navigation completed")
    }

    fun switchToTab(tab: AppTabType) {
        if (currentTab != tab) {
            currentTab = tab
            // Reset currentScreen to TransactionList when switching tabs
            // This prevents the "already on screen" guard from blocking navigation
            // when tabs trigger screen changes (like GOALS -> SavingsGoals)
            currentScreen = Screen.TransactionList()
            // Persist so reload restores the user's last active tab
            try { kotlinx.browser.localStorage.setItem("last_active_tab", tab.name) } catch (e: Exception) {}
            // Reset scroll position when switching tabs
            kotlinx.browser.window.scrollTo(0.0, 0.0)
            notifyTabListeners()
        }
    }

    fun setAccessToken(token: String) {
        accessToken = token
        // Keep AI categorization client in sync with the current auth token
        ml.setAIAuthToken(token)
        // Store token persistently for future app launches
        GlobalScope.launch {
            try {
                secureStorage.store("plaid_access_token", token)
                console.log("Access token stored in SecureStorage")
            } catch (e: Exception) {
                console.error("Failed to store access token: ${e.message}")
            }
        }
    }

    fun getAccessToken(): String? = accessToken

    fun setSelectedTransaction(transaction: Transaction) {
        selectedTransaction = transaction
    }

    fun getSelectedTransaction(): Transaction? = selectedTransaction

    // Wiz 3R card detail payload — set right before navigating to
    // Screen.Wiz3RCardDetail so the detail component can read the full card
    // without a re-fetch. Lost on page reload (acceptable for Phase 2a).
    private var selectedWiz3rCard: PersonalizedCardData? = null

    fun setSelectedWiz3rCard(card: PersonalizedCardData) {
        selectedWiz3rCard = card
    }

    fun getSelectedWiz3rCard(): PersonalizedCardData? = selectedWiz3rCard

    /** One-shot consume of a Screen.TransactionList.categoryFilter. After the
     *  category-aware auto-account-switch runs, this clears the screen's
     *  filter (without notifying listeners) so subsequent re-renders — like
     *  the one triggered by the user clicking a different account or hitting
     *  Clear — don't re-apply the filter and re-override their choice. */
    fun consumeCurrentScreenCategoryFilter(): String? {
        val s = currentScreen as? Screen.TransactionList ?: return null
        val v = s.categoryFilter ?: return null
        currentScreen = Screen.TransactionList(categoryFilter = null, openTransactionId = s.openTransactionId)
        return v
    }

    // One-shot: when a Wiz 3R card action navigates to the Budget tab to "Set a
    // budget for this category", the raw Plaid primary code is stashed here.
    // The Budget component reads + clears it on mount so a manual tab switch
    // later doesn't re-focus the stale category.
    private var pendingBudgetFocusCategory: String? = null

    fun setPendingBudgetFocusCategory(code: String?) {
        pendingBudgetFocusCategory = code
    }

    fun consumePendingBudgetFocusCategory(): String? {
        val v = pendingBudgetFocusCategory
        pendingBudgetFocusCategory = null
        return v
    }

    fun navigateToTransactionDetailWithTransaction(transaction: Transaction) {
        setSelectedTransaction(transaction)
        val transactionData =
            TransactionData(
                id = transaction.id,
                merchant = transaction.merchant,
                amount = transaction.amount,
                date = transaction.date.toISOString().substring(0, 10), // Convert to yyyy-MM-dd format
                category = transaction.category,
                account = transaction.account,
                isDebit = transaction.isDebit,
                aiConfidence = transaction.aiConfidence,
                categorySource = transaction.categorySource,
                confidenceLevel = transaction.confidenceLevel,  // Pass through the Plaid confidence level
                categoryLevel1 = transaction.categoryLevel1,
                categoryLevel2 = transaction.categoryLevel2,
                categoryLevel3 = transaction.categoryLevel3,
                // Goal assignment fields
                assignedGoalId = transaction.assignedGoalId,
                assignedGoalName = transaction.assignedGoalName,
                assignedGoalIcon = transaction.assignedGoalIcon,
                // Split/Distribute fields
                isSplitParent = transaction.isSplitParent,
                isDistributedParent = transaction.isDistributedParent,
                parentTransactionId = transaction.parentTransactionId,
                childType = transaction.childType,
                // User notes
                userNotes = transaction.userNotes,
                isExcluded = transaction.isExcluded,
            )
        navigateTo(Screen.TransactionDetail(transaction.id, transactionData))
    }

    fun navigateBack(): Boolean {
        if (currentScreen is Screen.Landing) return false

        // Screen.Landing is no longer a destination inside the SPA — the marketing
        // landing lives at / as static HTML. Any "back" that would naturally land
        // on the SPA's old Landing redirects to / instead.
        val fallback = when (currentScreen) {
            is Screen.TransactionDetail -> Screen.TransactionList()
            is Screen.SavingsGoals -> Screen.TransactionList()
            is Screen.Wiz3RCardDetail -> Screen.TransactionList()
            is Screen.Recommendations -> Screen.SavingsGoals(openCreateDialog = false)
            is Screen.TransactionList -> Screen.Login
            is Screen.AccountLinking -> Screen.Login
            is Screen.StatementImport -> Screen.AccountLinking()
            is Screen.Login -> { window.location.href = "/"; return true }
            is Screen.Register -> Screen.Login
            is Screen.CheckEmail -> Screen.Login
            is Screen.MfaVerification -> Screen.Login
            is Screen.ResetPassword -> Screen.Login
            is Screen.VerifyEmail -> Screen.Login
            is Screen.SubscribeSuccess, is Screen.SubscribeCancel -> Screen.Subscribe()
            // The paywall is reached from inside the app (a Pro-locked feature,
            // an upgrade nudge), so "Maybe Later" must put the user back exactly
            // where they were — not bounce them out to the marketing site and
            // make them navigate all the way back in. Only a visitor with no
            // in-app history (a /subscribe deep link) leaves the SPA.
            is Screen.Subscribe -> previousScreen ?: run {
                window.location.href = "/"
                return true
            }
            // Screens reachable from multiple places
            is Screen.Terms, is Screen.Privacy, is Screen.Security,
            is Screen.About, is Screen.Contact, is Screen.Team, is Screen.Guide -> {
                window.location.href = "/"
                return true
            }
            is Screen.Landing -> return false
        }

        val target = previousScreen ?: fallback
        if (target is Screen.Landing) {
            window.location.href = "/"
            return true
        }
        currentScreen = target
        previousScreen = null
        notifyListeners()
        return true
    }

    fun addNavigationListener(listener: (Screen) -> Unit) {
        listeners.add(listener)
    }

    fun removeNavigationListener(listener: (Screen) -> Unit) {
        listeners.remove(listener)
    }

    fun addTabListener(listener: (AppTabType) -> Unit) {
        tabListeners.add(listener)
    }

    fun removeTabListener(listener: (AppTabType) -> Unit) {
        tabListeners.remove(listener)
    }

    private fun notifyListeners() {
        listeners.forEach { it(currentScreen) }
    }

    private fun notifyTabListeners() {
        tabListeners.forEach { it(currentTab) }
    }

    fun logout() {
        // Synchronously invalidate the in-memory auth state and navigate.
        // Any rerender that follows this call will see the logged-out state
        // immediately, so the marketing menu (and any other auth-conditional
        // UI) renders correctly without a race against the persistent-storage
        // clear coroutine. SecureStorage gets cleared in the background;
        // its persistence isn't on the critical path for "the user is logged
        // out right now."
        ml.setAIAuthToken(null)
        accessToken = null
        ApiService.clearSharedSession()
        if (ReadOnlyGate.demoMode) {
            // Leaving the demo: drop the identity override — in memory AND in
            // sessionStorage, or the next boot would resume the demo the user
            // just left — and rebuild the goals singleton under the real
            // (logged-out) identity.
            auth.DemoUserOverride.userId = null
            auth.DemoUserOverride.authToken = null
            clearPersistedDemoSession()
            repository.resetWebSavingsGoalsRepositorySingleton()
        }
        ReadOnlyGate.setDemoMode(false)
        navigateTo(Screen.Login)
        GlobalScope.launch {
            try {
                secureStorage.clear()
                console.log("Logout: Cleared persisted tokens from SecureStorage")
            } catch (e: Exception) {
                console.error("Error clearing SecureStorage during logout: ${e.message}")
            }
        }
    }
}

// WebApp class removed — was dead code never instantiated from main().
// All rendering is handled by WebMainAppComponent below.

private const val DEMO_NOTICE_ID = "demo-notice"
private const val DEMO_TOKEN_KEY = "wiz3r_demo_token"
private const val DEMO_USER_ID_KEY = "wiz3r_demo_user_id"
private const val DEMO_EXPIRES_AT_KEY = "wiz3r_demo_expires_at"

/**
 * The demo identity, kept in `sessionStorage` so it survives a reload.
 *
 * Anything that reloads the page — the language toggle, the theme buttons — used
 * to drop a visitor straight back to the login screen, because the demo identity
 * lived only in memory. Nothing about that reset was the safety boundary: the
 * demo token is read-only server-side, which is what actually stops a visitor
 * touching the shared account.
 *
 * `sessionStorage`, not `localStorage`: the demo still ends with the tab, so it
 * never becomes a session the visitor forgets they are in.
 */
private fun persistDemoSession(token: String, userId: String, expiresAt: Long) {
    try {
        window.sessionStorage.setItem(DEMO_TOKEN_KEY, token)
        window.sessionStorage.setItem(DEMO_USER_ID_KEY, userId)
        window.sessionStorage.setItem(DEMO_EXPIRES_AT_KEY, expiresAt.toString())
    } catch (_: Exception) {}
}

private fun clearPersistedDemoSession() {
    try {
        window.sessionStorage.removeItem(DEMO_TOKEN_KEY)
        window.sessionStorage.removeItem(DEMO_USER_ID_KEY)
        window.sessionStorage.removeItem(DEMO_EXPIRES_AT_KEY)
    } catch (_: Exception) {}
}

/**
 * Maps a bare URL path to the marketing Screen it should render. Used by both
 * boot-time route resolution and the popstate handler so back/forward across
 * SPA marketing pages doesn't full-reload.
 */
private fun resolvePathScreen(pathname: String): Screen? = when (pathname) {
    "/security" -> Screen.Security
    "/privacy" -> Screen.Privacy
    "/terms" -> Screen.Terms
    "/guide" -> Screen.Guide
    "/about" -> Screen.About
    "/subscribe" -> Screen.Subscribe()
    "/contact" -> Screen.Contact
    "/team" -> Screen.Team
    else -> null
}

/** Reverse of resolvePathScreen — returns the canonical URL path for a Screen, or null. */
private fun pathForScreen(screen: Screen): String? = when (screen) {
    is Screen.Security -> "/security"
    is Screen.Privacy -> "/privacy"
    is Screen.Terms -> "/terms"
    is Screen.Guide -> "/guide"
    is Screen.About -> "/about"
    is Screen.Subscribe -> "/subscribe"
    is Screen.Contact -> "/contact"
    is Screen.Team -> "/team"
    else -> null
}

fun main() {
    console.log("Application starting... v=debug-23")

    // Marketing-capture demo flags: parse ?demo=1 / ?tour-auto=N from
    // the very first URL and persist to sessionStorage. Without this,
    // a redirect through /login or any tab nav strips the query
    // params before GuideOverlay's constructor (or autoStartTour
    // IfDemoMode) ever sees them — and the user gets the bigger fonts
    // / auto-advance / chapter markers on zero pages. Doing it at
    // boot guarantees the flags survive every subsequent navigation
    // for the life of the tab.
    run {
        val q = window.location.search
        if (q.contains("demo=1") || q.contains("demo=true")) {
            window.sessionStorage.setItem("wiz3r_demo", "1")
            console.log("🎬 Marketing demo mode armed (sessionStorage)")
            // Force Grid dashboard mode for the recording — the auto-
            // started tour covers Grid-only widgets (Customize toolbar,
            // saved layouts, lock, drag/resize). Standard mode would
            // skip those steps and leave the recording with broken
            // anchors. Persisted to localStorage so it sticks across
            // logouts within the demo profile.
            localStorage.setItem("dashboard_mode", "grid")
            console.log("🎬 Dashboard mode pinned to 'grid' for demo")
        }
        Regex("""[?&]tour-auto=(\d+)""").find(q)?.groupValues?.get(1)?.let { autoMs ->
            window.sessionStorage.setItem("wiz3r_tour_auto", autoMs)
            console.log("🎬 Tour auto-advance armed at ${autoMs}ms")
        }
        // Cosmetic Monthly-Overview year relabel for the hero time-jump
        // (?demo-year-shift=-1). Armed here at boot for the same reason as the
        // flags above: the /login redirect + pathname-only replaceState strip
        // the query before the dashboard renders. Absent → clear any stale
        // value so an S3 capture (no param) shows the real current year.
        val yearShift = Regex("""[?&]demo-year-shift=(-?\d+)""").find(q)?.groupValues?.get(1)
        if (yearShift != null) {
            window.sessionStorage.setItem("wiz3r_demo_year_shift", yearShift)
            console.log("🎬 Monthly Overview year-label shift armed: $yearShift")
        } else {
            window.sessionStorage.removeItem("wiz3r_demo_year_shift")
        }
        // Same rescue-at-boot for tour-manual-start=1. Without this the
        // /login redirect strips the query string BEFORE any page's
        // autoStartTourIfDemoMode call runs — the flag would only work
        // if you landed directly on the target page (impossible in the
        // real flow because login is always the first landing).
        if (q.contains("tour-manual-start=1")) {
            window.sessionStorage.setItem("wiz3r_tour_manual_start", "1")
            console.log("🎬 Tour manual-start armed — page intros before ⓘ click")
        }
    }

    // Initialize locale manager early
    val settingsManager = settings.SettingsManager()
    LocaleManager.initialize(settingsManager)

    // ?lang= overrides the stored preference for THIS page load only.
    //
    // The mobile apps send their own language when they open a legal page here
    // (see SETTINGS_VIEW_ON_WEB), and the page must come up in the language the
    // app is running in, not in whatever this browser happens to have saved —
    // which for a fresh browser is English regardless.
    //
    // Deliberately Strings.setLocale, not LocaleManager.setLocale: the former is
    // in-memory only. Following a French link must not silently rewrite the
    // language this browser uses from then on.
    Regex("""[?&]lang=([A-Za-z-]+)""").find(window.location.search)?.groupValues?.get(1)?.let { requested ->
        val forced = i18n.AppLocale.fromCode(requested.take(2))
        if (forced != LocaleManager.getCurrentLocale()) {
            i18n.Strings.setLocale(forced)
            console.log("Locale forced to ${forced.code} by ?lang= (this page load only)")
        }
    }
    console.log("Locale initialized: ${LocaleManager.getCurrentLocale().code}")

    WebTheme.initialize()

    val navigationManager = WebNavigationManager()
    val apiService = ApiService()
    val app = WebMainAppComponent(navigationManager, apiService)

    // Check for Stripe redirect hash before mounting
    val hash = window.location.hash
    val search = window.location.search

    // Extract email verification params from URL NOW, before we clear them,
    // so they can be passed to VerifyEmailPage without re-reading the URL later.
    val emailVerifiedParam: Boolean? = when {
        search.contains("email-verified=true") -> true
        search.contains("email-verified=false") -> false
        else -> null
    }
    val verifyTokenParam: String? = when {
        search.contains("token=") -> search.substringAfter("token=").substringBefore("&").takeIf { it.isNotBlank() }
        hash.contains("token=") -> hash.substringAfter("token=").substringBefore("&").takeIf { it.isNotBlank() }
        else -> null
    }

    // Check both hash-based (#security) and path-based (/security) URLs
    val pathname = window.location.pathname
    val pathScreen = resolvePathScreen(pathname)
    val hashScreen: Screen? = when {
        hash.contains("subscribe-success") -> Screen.SubscribeSuccess
        hash.contains("subscribe-cancel") -> Screen.SubscribeCancel
        hash == "#subscribe" || hash == "#subscribe/" -> Screen.Subscribe()
        hash == "#guide" || hash == "#guide/" -> Screen.Guide
        hash == "#privacy" || hash == "#privacy/" -> Screen.Privacy
        hash == "#terms" || hash == "#terms/" -> Screen.Terms
        hash == "#security" || hash == "#security/" -> Screen.Security
        hash == "#about" || hash == "#about/" -> Screen.About
        hash == "#contact" || hash == "#contact/" -> Screen.Contact
        hash == "#team" || hash == "#team/" -> Screen.Team
        hash.startsWith("#verify") -> Screen.VerifyEmail(verifyToken = verifyTokenParam)
        // Server-side redirect result (new flow: server verifies then redirects here)
        emailVerifiedParam != null -> Screen.VerifyEmail(emailVerified = emailVerifiedParam)
        // Legacy query-param based verify link (JS-side verification, kept for backward compat)
        search.contains("verify=true") -> Screen.VerifyEmail(verifyToken = verifyTokenParam)
        // Email-link deep link: /?tx=<id> from the notification "Edit in app →" rows.
        // Land on the Transactions tab and auto-open this transaction's detail.
        search.contains("tx=") -> {
            val txId = search.substringAfter("tx=").substringBefore("&").takeIf { it.isNotBlank() }
            if (txId != null) Screen.TransactionList(openTransactionId = txId) else null
        }
        // /?home — legacy from when the marketing landing lived in the SPA.
        // The static marketing landing now owns /, so a `?home` query at /app
        // means "take me back to /" (a full-page navigation, not an SPA route).
        search.contains("home") -> {
            window.location.href = "/"
            null
        }
        // Clean URL paths (e.g., wiz3r.com/security). Marketing path → Screen
        // is shared with popstate via resolvePathScreen.
        pathScreen != null -> pathScreen
        // /app/login lands the user on the Login screen directly, skipping
        // the SPA's redundant marketing landing (the marketing landing now
        // lives at / as static HTML — visiting /app means "I want to do
        // something in the app", and the most common intent is sign in).
        pathname == "/app/login" -> Screen.Login
        else -> null
    }
    if (hashScreen != null) {
        navigationManager.navigateTo(hashScreen)
        app.overrideInitialScreen = hashScreen
    }
    // Clear hash after handling (preserve verify token in hash only if JS-side verification needed)
    if (hash.isNotEmpty() && !hash.startsWith("#verify")) window.location.hash = ""
    // Normalise /app/<sub-path> URLs to bare /app once we've consumed the
    // intent. Avoids leaving /app/login or /app/dashboard etc. in the
    // address bar after the user has moved on inside the SPA.
    if (pathname.startsWith("/app/") && pathname != "/app/") {
        window.history.replaceState(null, "", "/app")
    }
    // Clear email-verified query params immediately — result is now stored in the Screen object
    if (search.contains("email-verified=") || search.contains("verify=true") || search.contains("tx=") || search.contains("home")) {
        window.history.replaceState(null, "", window.location.pathname)
    }

    val root = document.getElementById("root") as? HTMLElement
    if (root != null) {
        app.mount(root)
    } else {
        console.error("Root element not found!")
    }

    // Back/forward navigation across SPA marketing pages: resolve the new path
    // and route the SPA without a full reload. If the path isn't a known
    // marketing route (e.g. user backed out to /app), full-reload — the boot
    // logic above re-runs cleanly under the new URL.
    window.addEventListener("popstate", {
        val newPath = window.location.pathname
        val target = resolvePathScreen(newPath)
        if (target != null) {
            app.navigateAndRerender(target)
        } else {
            window.location.reload()
        }
    })
}

/**
 * Web implementation of the main app with tab navigation
 */
class WebMainAppComponent(
    private val navigationManager: WebNavigationManager,
    private val apiService: ApiService,
) {
    private var rootContainer: HTMLElement? = null
    private var contentElement: HTMLElement? = null
    private val tabContainers = mutableMapOf<AppTabType, HTMLElement>()
    private var activeTabContainer: AppTabType? = null
    private var currentTab = AppTabType.TRANSACTIONS
    private var selectedAccountId: String? = null
    // Sticky category filter carried across re-renders on the TRANSACTIONS tab
    // (e.g. when the user clicks another account). Set by onAccountSwitch from
    // the live component's filter so the new account inherits it, cleared by
    // onCategoryFilterCleared. Distinct from the one-shot Wiz 3R intent so
    // user-driven re-renders never re-trigger the auto-account-switch.
    private var stickyTransactionCategoryFilter: String? = null
    private var accounts: List<PlaidAccount> = emptyList()
    private val secureStorage = SecureStorageFactory.create()
    private var cachedTransactions: List<Transaction> = emptyList()
    private var isLoggingIn = false
    private var loginError: String? = null
    private val settingsManager = settings.SettingsManager()
    var overrideInitialScreen: Screen? = null
    private var sessionExpiredMessage: String? = null
    private var justLinkedPlaid: Boolean = false  // Prevents redirect back to AccountLinking
    private var isDemoMode: Boolean = false  // User skipped account linking, show demo data
    private var isReadOnly: Boolean = false  // Subscription expired — block writes
    private var isTrialing: Boolean = false  // User is on a free trial (not yet subscribed)
    private var userCategoryColors: Map<String, String> = emptyMap()
    private var customCategories: List<CustomCategoryData> = emptyList()
    private var subscriptionTier: String = "standard"
    private var trialEndsAt: String? = null
    // Access: pro OR trialing. Governs every LOCK/gate — trialing keeps full use.
    val isProTier: Boolean get() = subscriptionTier == "pro" || isTrialing
    // Tag visibility: everyone who isn't a *permanent* Pro (standard AND trialing).
    // Trialing users see the "PRO" pills as a "you'll lose this at trial end" nudge,
    // even though the feature stays unlocked for them.
    val showProTag: Boolean get() = subscriptionTier != "pro"
    // Already paying for Standard → the paywall should offer only the Pro upgrade,
    // not re-sell Standard. Excludes trialing (they're choosing a post-trial plan)
    // and expired/free (isReadOnly — they can still pick either tier).
    val isActiveStandardSubscriber: Boolean
        get() = subscriptionTier == "standard" && !isReadOnly && !isTrialing
    private var dashboardMode: String = localStorage.getItem("dashboard_mode") ?: "standard" // "standard" or "grid"

    private var isInitializing = true
    private var tabListenerAdded = false
    private var pendingScrollRestore: Double? = null

    /** How long the demo notice stays up after each page change. */
    private val demoNoticeMs = 5000
    /** Retirement timer for the notice currently on screen, so arriving at a new
     *  page restarts the five seconds instead of inheriting the old countdown. */
    private var demoNoticeTimer: Int? = null

    init {
        console.log("WebMainAppComponent initialized. selectedAccountId: $selectedAccountId")

        // Configure server-side AI categorization (same origin as the web app)
        ml.configureAICategoryClient(kotlinx.browser.window.location.origin)

        // Set up session expired callback
        ApiService.setSessionExpiredCallback {
            console.log("Session expired callback triggered")
            GlobalScope.launch {
                apiService.clearSession()
                navigationManager.logout()
                sessionExpiredMessage = StringKey.TRANSACTION_SESSION_EXPIRED.tr()
                rerender()
            }
        }

        // Listen for screen changes
        navigationManager.addNavigationListener { screen ->
            console.log("Screen changed to: $screen")
            // Every screen the visitor lands on re-announces the demo.
            raiseDemoNotice()
            // Demo-mode: emit [INTRO t=<ms> SCREEN] so the SRT generator
            // can time the page-intro caption ("This is your dashboard…")
            // against the video. Fires once per screen per session,
            // BEFORE the tour starts, so Eric can deliver the intro
            // line to camera and then click ⓘ.
            //
            // Only the screens with a matching entry in
            // demo_narration.md's "Page intros" table need to fire.
            // Studio + Debt Studio are modals opened from Goals, not
            // nav transitions — those emit their own intro at modal
            // open time (see WebSavingsGoalsComponent.openStudioForGoal).
            when (screen) {
                is Screen.SavingsGoals      -> emitDemoIntro("GOALS")
                is Screen.TransactionList   -> emitDemoIntro("TRANSACTIONS")
                // Dashboard/Budget are web tabs (AppTabType), not Screen subclasses.
                // The old is-Screen.Dashboard / is-Screen.BudgetDashboard branches
                // resolved to the SHARED klib's Screen (duplicate root-package FQN),
                // never fired here, and crashed the production Kotlin/JS compiler
                // (Fir2Ir) on a cold build. Removed. Do not re-add without first
                // de-colliding the two Screen hierarchies into distinct packages.
                else -> Unit
            }
            rerender()
        }

        // Check for stored token
        GlobalScope.launch {
            checkStoredTokenAndNavigate()
        }
    }
    
    fun mount(container: HTMLElement) {
        rootContainer = container
        rerender()
    }
    
    private fun rerender() {
        val container = rootContainer ?: return
        container.innerHTML = ""
        container.appendChild(render())
    }

    /** Entry point for external (browser-driven) route changes such as popstate. */
    fun navigateAndRerender(screen: Screen) {
        navigationManager.navigateTo(screen)
        rerender()
    }

    private suspend fun checkStoredTokenAndNavigate() {
        try {
            val storedToken = secureStorage.retrieve("plaid_access_token")
            val storedAppToken = secureStorage.retrieve("app_auth_token")
            val storedUserId = secureStorage.retrieve("app_user_id")
            val storedExpiresAt = secureStorage.retrieve("app_token_expires_at")?.toLongOrNull() ?: 0L

            if (storedAppToken != null && storedUserId != null) {
                // Check if token has already expired
                val currentTime = (kotlin.js.Date.now() / 1000).toLong()
                if (storedExpiresAt > 0 && currentTime > storedExpiresAt) {
                    console.log("Stored token has expired, clearing session")
                    apiService.clearSession()
                    navigationManager.logout()
                    showSessionExpiredMessage()
                    return
                }

                console.log("Found stored app token, restoring session")
                apiService.restoreSession(storedAppToken, storedUserId, storedExpiresAt)

                // CRITICAL: Sync userId to UserIdManager for shared repository access
                UserIdManager().setUserId(storedUserId)
                console.log("✅ WebApp: Restored userId to UserIdManager: $storedUserId")

                // Try to refresh if token is close to expiring; track result to sync AI token
                var effectiveAppToken = storedAppToken
                if (storedExpiresAt > 0) {
                    try {
                        val refreshedToken = apiService.refreshTokenIfNeeded()
                        if (refreshedToken != null) {
                            // Token was refreshed - save new token to storage
                            console.log("Token was refreshed, saving new token to storage")
                            secureStorage.store("app_auth_token", refreshedToken.token)
                            secureStorage.store("app_token_expires_at", refreshedToken.expiresAtEpochSeconds.toString())
                            effectiveAppToken = refreshedToken.token
                        }
                    } catch (e: SessionExpiredException) {
                        console.log("Session expired during refresh, clearing session")
                        apiService.clearSession()
                        navigationManager.logout()
                        showSessionExpiredMessage()
                        return
                    }
                }

                // Set Plaid access token in NavigationManager if available (for Plaid-linked features)
                if (storedToken != null) {
                    navigationManager.setAccessToken(storedToken)
                }

                // CRITICAL: Sync the app JWT (not the Plaid token) to the AI auth token store.
                // navigationManager.setAccessToken() above stores the Plaid token which would
                // overwrite the AI token with the wrong credential, so we explicitly fix it here.
                ml.setAIAuthToken(effectiveAppToken)

                // Load subscription status BEFORE navigating (no race condition)
                try {
                    val status = apiService.getSubscriptionStatus()
                    isReadOnly = status.status != "active" && status.status != "trialing"
                    isTrialing = status.status == "trialing"
                    subscriptionTier = status.tier
                    trialEndsAt = status.trialEndsAt
                } catch (e: Exception) {
                    console.log("Subscription status unavailable: ${e.message}")
                    isReadOnly = false
                    isTrialing = false
                    // Fail open: never block a user behind a paywall due to an API error
                    subscriptionTier = "pro"
                }

                // Load user custom category colors
                try {
                    val colorJson = apiService.getSetting("custom_category_colors")
                    if (!colorJson.isNullOrBlank()) {
                        userCategoryColors = kotlinx.serialization.json.Json.decodeFromString(colorJson)
                    }
                } catch (_: Exception) {}

                // Load user custom categories so every icon-render site can look up the
                // user-picked icon instead of falling through to the default shopping bag.
                try {
                    customCategories = apiService.getCustomCategories()
                } catch (_: Exception) {}

                // Check if language toggle requested staying on landing page
                val stayOnLanding = try {
                    val flag = kotlinx.browser.localStorage.getItem("stay_on_landing")
                    kotlinx.browser.localStorage.removeItem("stay_on_landing")
                    flag == "true"
                } catch (_: Exception) { false }

                // Restore last active tab (default: TRANSACTIONS) unless a hash override was set
                val override = overrideInitialScreen
                if (stayOnLanding) {
                    // Legacy from the SPA-rendered landing — the static landing
                    // owns / now, so honor the user's "stay on landing" intent
                    // by leaving the SPA entirely.
                    overrideInitialScreen = null
                    window.location.href = "/"
                    return
                } else if (override != null && override !is Screen.VerifyEmail) {
                    overrideInitialScreen = null
                    navigationManager.navigateTo(override)
                } else {
                    overrideInitialScreen = null
                    val savedTabName = try { kotlinx.browser.localStorage.getItem("last_active_tab") } catch (e: Exception) { null }
                    val restoredTab = try { if (savedTabName != null) AppTabType.valueOf(savedTabName) else AppTabType.TRANSACTIONS } catch (e: Exception) { AppTabType.TRANSACTIONS }
                    navigationManager.switchToTab(restoredTab)
                    navigationManager.navigateTo(Screen.TransactionList())
                }
            } else {
                // Unauthenticated default at /app is the Login screen.
                // The marketing landing now lives at / as static HTML, so
                // there's no point showing a redundant SPA-rendered marketing
                // landing here. The Login form has a "create account" toggle
                // so both "Login" and "Get Started" flows land on it cleanly.
                console.log("No stored app token found, showing login")
                val publicOverride = overrideInitialScreen
                overrideInitialScreen = null
                // A demo running before a reload is resumed rather than dropped.
                // An explicit public-page request (Terms, Guide, …) still wins —
                // hence the null check — but otherwise a visitor who reloaded
                // onto /app was in the demo, not on their way out of it.
                if (publicOverride == null && restoreDemoSessionIfAny()) {
                    val savedTabName = try {
                        kotlinx.browser.localStorage.getItem("last_active_tab")
                    } catch (_: Exception) { null }
                    val restoredTab = try {
                        if (savedTabName != null) AppTabType.valueOf(savedTabName) else AppTabType.TRANSACTIONS
                    } catch (_: Exception) { AppTabType.TRANSACTIONS }
                    navigationManager.switchToTab(restoredTab)
                    navigationManager.navigateTo(Screen.TransactionList())
                } else if (publicOverride is Screen.Guide || publicOverride is Screen.Terms
                    || publicOverride is Screen.Privacy || publicOverride is Screen.Security || publicOverride is Screen.About || publicOverride is Screen.Contact || publicOverride is Screen.Team) {
                    navigationManager.navigateTo(publicOverride)
                } else {
                    // Default for /app (and /app/login) when not signed in
                    navigationManager.navigateTo(Screen.Login)
                }
            }
        } catch (e: Exception) {
            console.error("Error checking stored token: ${e.message}")
            navigationManager.navigateTo(Screen.Login)
        } finally {
            isInitializing = false
            rerender()
        }
    }

    private fun showSessionExpiredMessage() {
        // Show a notification to the user
        sessionExpiredMessage = StringKey.TRANSACTION_SESSION_EXPIRED.tr()
        rerender()
    }

    private fun menuNavigate(page: String) {
        if (page == "subscribe") {
            showSubscribeModal()
            return
        }
        if (page == "guide") {
            showGuideModal()
            return
        }
        // The hamburger menu's wiz3r logo emits "landing" — that means
        // "go home", which now lives at / as static HTML. Anything else
        // unrecognised also falls back to /.
        val screen = when (page) {
            "team" -> Screen.Team
            "about" -> Screen.About
            "contact" -> Screen.Contact
            "privacy" -> Screen.Privacy
            "terms" -> Screen.Terms
            "security" -> Screen.Security
            else -> {
                window.location.href = "/"
                return
            }
        }
        // Sync URL bar so refresh/copy-paste lands on the visible page,
        // and back/forward replays the marketing nav stack.
        pathForScreen(screen)?.let { path ->
            if (window.location.pathname != path) {
                window.history.pushState(null, "", path)
            }
        }
        navigationManager.navigateTo(screen)
        rerender()
    }

    private fun showSubscribeModal() {
        // If one is already open, bail out
        if (document.getElementById("subscribe-modal-overlay") != null) return

        val overlay = document.createElement("div") as HTMLElement
        overlay.id = "subscribe-modal-overlay"
        overlay.setAttribute("style",
            "position: fixed; inset: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(6px); " +
            "-webkit-backdrop-filter: blur(6px); z-index: 999; display: flex; align-items: flex-start; " +
            "justify-content: center; overflow-y: auto; padding: 40px 20px; animation: modalFadeIn 0.25s ease-out;"
        )

        val card = document.createElement("div") as HTMLElement
        card.setAttribute("style",
            "position: relative; max-width: 920px; width: 100%; border-radius: 20px; " +
            "background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%); " +
            "box-shadow: 0 0 60px rgba(0,217,255,0.25), 0 20px 60px rgba(0,0,0,0.5); " +
            "animation: modalScaleIn 0.3s cubic-bezier(.2,.8,.2,1);"
        )

        val subscribePage = SubscribePage(
            onBackClick = { closeSubscribeModal() },
            isAlreadyTrialing = isTrialing,
            proOnly = isActiveStandardSubscriber,
            onNavigate = null,
            isLoggedIn = apiService.isAuthenticated(),
            onAppNavigate = null,
            asModal = true,
        )
        val subscribeElement = subscribePage.render()
        card.appendChild(subscribeElement)
        subscribePage.attachEventListeners(subscribeElement)

        val closeBtn = document.createElement("div") as HTMLElement
        closeBtn.innerHTML = "&times;"
        closeBtn.setAttribute("style",
            "position: absolute; top: 16px; right: 20px; color: #99a0a6; font-size: 28px; " +
            "cursor: pointer; z-index: 10; line-height: 1; transition: color 0.2s; user-select: none;"
        )
        closeBtn.addEventListener("click", { closeSubscribeModal() })
        card.appendChild(closeBtn)

        overlay.appendChild(card)
        overlay.addEventListener("click", { e ->
            if (e.target == overlay) closeSubscribeModal()
        })

        if (document.getElementById("register-modal-styles") == null) {
            val style = document.createElement("style") as HTMLElement
            style.id = "register-modal-styles"
            style.textContent = """
                @keyframes modalFadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes modalScaleIn { from { opacity: 0; transform: scale(0.92) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
            """.trimIndent()
            document.head?.appendChild(style)
        }

        document.body?.appendChild(overlay)
    }

    private fun closeSubscribeModal() {
        document.getElementById("subscribe-modal-overlay")?.let { it.parentNode?.removeChild(it) }
    }

    private fun menuAppNavigate(tab: String) {
        when (tab) {
            "transactions" -> navigationManager.switchToTab(AppTabType.TRANSACTIONS)
            "dashboard" -> navigationManager.switchToTab(AppTabType.DASHBOARD)
            "goals" -> navigationManager.switchToTab(AppTabType.GOALS)
            "budget" -> navigationManager.switchToTab(AppTabType.BUDGET_PLANNING)
            "settings" -> navigationManager.switchToTab(AppTabType.SETTINGS)
            "logout" -> {
                GlobalScope.launch {
                    apiService.clearSession()
                    navigationManager.logout()
                    rerender()
                }
                return
            }
        }
        navigationManager.navigateTo(Screen.TransactionList())
        rerender()
    }

    fun render(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.className = "app-container"
        
        if (isInitializing) {
            container.innerHTML = """
                <div style="display: flex; justify-content: center; align-items: center; height: 100dvh; flex-direction: column;">
                    <div style="width: 50px; height: 50px; border: 5px solid #f3f3f3; border-top: 5px solid #3498db; border-radius: 50%; animation: spin 1s linear infinite; margin-bottom: 20px;"></div>
                    <p style="color: #666; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;">Loading wiz≡r...</p>
                    <style>
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    </style>
                </div>
            """
            return container
        }
        
        // Marketing pages always use dark theme; app pages use user preference
        val currentScreen = navigationManager.getCurrentScreen()
        val isMarketingPage = currentScreen is Screen.Landing || currentScreen is Screen.Terms
            || currentScreen is Screen.Privacy || currentScreen is Screen.Security
            || currentScreen is Screen.About || currentScreen is Screen.Contact
            || currentScreen is Screen.Team || currentScreen is Screen.Guide
            || currentScreen is Screen.Subscribe || currentScreen is Screen.SubscribeSuccess
            || currentScreen is Screen.SubscribeCancel
            || currentScreen is Screen.Login || currentScreen is Screen.Register
            || currentScreen is Screen.CheckEmail || currentScreen is Screen.ResetPassword
            || currentScreen is Screen.VerifyEmail || currentScreen is Screen.MfaVerification
        if (isMarketingPage) {
            WebTheme.forceDark()
        } else {
            WebTheme.restorePreference()
        }

        when (val screen = currentScreen) {
            is Screen.Landing -> {
                // Check if user is logged in (has stored auth token)
                val isLoggedIn = apiService.isAuthenticated()

                val landingPage = LandingPage(
                    onLoginClick = {
                        navigationManager.navigateTo(Screen.Login)
                        rerender()
                    },
                    onGetStartedClick = {
                        navigationManager.navigateTo(Screen.Register)
                        rerender()
                    },
                    onTermsClick = {
                        navigationManager.navigateTo(Screen.Terms)
                        rerender()
                    },
                    onPrivacyClick = {
                        navigationManager.navigateTo(Screen.Privacy)
                        rerender()
                    },
                    onSecurityClick = {
                        navigationManager.navigateTo(Screen.Security)
                        rerender()
                    },
                    onAboutClick = {
                        navigationManager.navigateTo(Screen.About)
                        rerender()
                    },
                    onContactClick = {
                        navigationManager.navigateTo(Screen.Contact)
                        rerender()
                    },
                    onTeamClick = {
                        navigationManager.navigateTo(Screen.Team)
                        rerender()
                    },
                    onGuideClick = {
                        navigationManager.navigateTo(Screen.Guide)
                        rerender()
                    },
                    isLoggedIn = isLoggedIn,
                    onDashboardClick = {
                        // Go to Dashboard tab when logged in user clicks the icon
                        navigationManager.switchToTab(AppTabType.DASHBOARD)
                        navigationManager.navigateTo(Screen.TransactionList())
                        rerender()
                    },
                    onNavClick = { navId ->
                        when (navId) {
                            "Activity" -> {
                                navigationManager.switchToTab(AppTabType.TRANSACTIONS)
                                navigationManager.navigateTo(Screen.TransactionList())
                            }
                            "Dashboard" -> {
                                navigationManager.switchToTab(AppTabType.DASHBOARD)
                                navigationManager.navigateTo(Screen.TransactionList())
                            }
                            "Goals" -> {
                                navigationManager.switchToTab(AppTabType.GOALS)
                                navigationManager.switchToTab(AppTabType.GOALS)
                            }
                            "Budget" -> {
                                navigationManager.switchToTab(AppTabType.BUDGET_PLANNING)
                                navigationManager.navigateTo(Screen.TransactionList())
                            }
                        }
                        rerender()
                    },
                    onSettingsClick = {
                        navigationManager.switchToTab(AppTabType.SETTINGS)
                        navigationManager.navigateTo(Screen.TransactionList())
                        rerender()
                    },
                    onSubscribeClick = {
                        navigationManager.navigateTo(Screen.Subscribe())
                        rerender()
                    },
                    onSubscribeMenuClick = { showSubscribeModal() },
                    onLogout = { menuAppNavigate("logout") },
                )
                val landingElement = landingPage.render()
                container.appendChild(landingElement)
                landingPage.attachEventListeners(landingElement)
            }
            is Screen.Terms -> {
                val loggedIn = apiService.isAuthenticated()
                val termsPage = TermsPage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    onNavigate = ::menuNavigate,
                    currentPage = "terms",
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                val termsElement = termsPage.render()
                container.appendChild(termsElement)
                termsPage.attachEventListeners(termsElement)
            }
            is Screen.Privacy -> {
                val loggedIn = apiService.isAuthenticated()
                val privacyPage = PrivacyPage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    onNavigate = ::menuNavigate,
                    currentPage = "privacy",
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                val privacyElement = privacyPage.render()
                container.appendChild(privacyElement)
                privacyPage.attachEventListeners(privacyElement)
            }
            is Screen.Security -> {
                val loggedIn = apiService.isAuthenticated()
                val securityPage = SecurityPage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    onNavigate = ::menuNavigate,
                    currentPage = "security",
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                val securityElement = securityPage.render()
                container.appendChild(securityElement)
                securityPage.attachEventListeners(securityElement)
            }
            is Screen.About -> {
                val loggedIn = apiService.isAuthenticated()
                val aboutPage = AboutPage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    onNavigate = ::menuNavigate,
                    currentPage = "about",
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                val aboutElement = aboutPage.render()
                container.appendChild(aboutElement)
                aboutPage.attachEventListeners(aboutElement)
            }
            is Screen.Contact -> {
                val loggedIn = apiService.isAuthenticated()
                val contactPage = ContactPage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    onNavigate = ::menuNavigate,
                    currentPage = "contact",
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                val contactElement = contactPage.render()
                container.appendChild(contactElement)
                contactPage.attachEventListeners(contactElement)
            }
            is Screen.Team -> {
                val loggedIn = apiService.isAuthenticated()
                val teamPage = TeamPage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    onNavigate = ::menuNavigate,
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                container.appendChild(teamPage.render())
            }
            is Screen.Guide -> {
                val loggedIn = apiService.isAuthenticated()
                val guidePage = GuidePage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    onNavigate = ::menuNavigate,
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                val guideElement = guidePage.render()
                container.appendChild(guideElement)
                guidePage.attachEventListeners(guideElement)
            }
            is Screen.Subscribe -> {
                val screen = navigationManager.getCurrentScreen() as Screen.Subscribe
                val loggedIn = apiService.isAuthenticated()
                val subscribePage = SubscribePage(
                    onBackClick = { navigationManager.navigateBack(); rerender() },
                    isAlreadyTrialing = isTrialing,
                    proOnly = screen.proOnly || isActiveStandardSubscriber,
                    onNavigate = ::menuNavigate,
                    isLoggedIn = loggedIn,
                    onAppNavigate = if (loggedIn) ::menuAppNavigate else null,
                )
                val subscribeElement = subscribePage.render()
                container.appendChild(subscribeElement)
                subscribePage.attachEventListeners(subscribeElement)
            }
            is Screen.SubscribeSuccess -> {
                val successPage = SubscribeSuccessPage(
                    onContinueClick = {
                        navigationManager.navigateTo(Screen.TransactionList())
                        rerender()
                    },
                )
                val successElement = successPage.render()
                container.appendChild(successElement)
                successPage.attachEventListeners(successElement)
            }
            is Screen.SubscribeCancel -> {
                val cancelPage = SubscribeCancelPage(
                    onRetryClick = {
                        navigationManager.navigateTo(Screen.Subscribe())
                        rerender()
                    },
                    onBackClick = {
                        navigationManager.navigateTo(Screen.Landing)
                        rerender()
                    },
                )
                val cancelElement = cancelPage.render()
                container.appendChild(cancelElement)
                cancelPage.attachEventListeners(cancelElement)
            }
            is Screen.Login -> {
                // Show session expired message if present, otherwise show login error
                val displayMessage = sessionExpiredMessage ?: loginError
                val loginComponent = LoginComponent(
                    isLoading = isLoggingIn,
                    errorMessage = displayMessage,
                    onLogin = { username, password ->
                        // Clear session expired message on new login attempt
                        sessionExpiredMessage = null
                        handleLogin(username, password)
                    },
                    onCreateAccount = {
                        sessionExpiredMessage = null
                        navigationManager.navigateTo(Screen.Register)
                        rerender()
                    },
                    onBackToLanding = {
                        sessionExpiredMessage = null
                        navigationManager.navigateTo(Screen.Landing)
                        rerender()
                    },
                    onForgotPassword = {
                        sessionExpiredMessage = null
                        navigationManager.navigateTo(Screen.ResetPassword)
                        rerender()
                    },
                    onTryDemo = {
                        sessionExpiredMessage = null
                        handleDemoSession()
                    }
                )
                container.appendChild(loginComponent.render())
            }
            is Screen.ResetPassword -> {
                val resetPage = ResetPasswordPage(
                    onBackToLogin = {
                        navigationManager.navigateTo(Screen.Login)
                        rerender()
                    }
                )
                container.appendChild(resetPage.render())
            }
            is Screen.VerifyEmail -> {
                val verifyPage = VerifyEmailPage(
                    onLoginClick = {
                        navigationManager.navigateTo(Screen.Login)
                        rerender()
                    },
                    emailVerified = screen.emailVerified,
                    verifyToken = screen.verifyToken,
                )
                container.appendChild(verifyPage.render())
            }
            is Screen.CheckEmail -> {
                val checkEmailPage = CheckEmailPage(
                    email = screen.email,
                    apiService = apiService,
                    onLoginClick = {
                        navigationManager.navigateTo(Screen.Login)
                        rerender()
                    }
                )
                container.appendChild(checkEmailPage.render())
            }
            is Screen.MfaVerification -> {
                val mfaComponent = MfaVerificationComponent(
                    tempToken = screen.tempToken,
                    isLoading = isLoggingIn,
                    errorMessage = loginError,
                    onVerify = { code ->
                        handleMfaVerification(screen.tempToken, code)
                    },
                    onBackToLogin = {
                        navigationManager.navigateTo(Screen.Login)
                        rerender()
                    }
                )
                container.appendChild(mfaComponent.render())
            }
            is Screen.Register -> {
                val registerComponent = RegisterComponent(
                    isLoading = isLoggingIn,
                    errorMessage = loginError,
                    onRegister = { registrationData ->
                        handleRegisterWithData(registrationData)
                    },
                    onBackToLogin = {
                        navigationManager.navigateTo(Screen.Login)
                        rerender()
                    },
                    onBackToLanding = {
                        navigationManager.navigateTo(Screen.Landing)
                        rerender()
                    }
                )
                // Render as modal overlay: backdrop behind + register card on top
                val overlay = document.createElement("div") as HTMLElement
                overlay.className = "register-modal-backdrop"
                overlay.setAttribute("style",
                    "position: fixed; inset: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(6px); " +
                    "-webkit-backdrop-filter: blur(6px); z-index: 999; display: flex; align-items: flex-start; " +
                    "justify-content: center; overflow-y: auto; padding: 40px 20px; animation: modalFadeIn 0.25s ease-out;"
                )
                val card = document.createElement("div") as HTMLElement
                card.className = "register-modal-card"
                card.setAttribute("style",
                    "position: relative; max-width: 520px; width: 100%; border-radius: 20px; " +
                    "box-shadow: 0 0 60px rgba(0,217,255,0.25), 0 20px 60px rgba(0,0,0,0.5); " +
                    "animation: modalScaleIn 0.3s cubic-bezier(.2,.8,.2,1);"
                )
                card.appendChild(registerComponent.render())
                // Close button in top-right
                val closeBtn = document.createElement("div") as HTMLElement
                closeBtn.innerHTML = "&times;"
                closeBtn.setAttribute("style",
                    "position: absolute; top: 16px; right: 20px; color: #99a0a6; font-size: 28px; " +
                    "cursor: pointer; z-index: 10; line-height: 1; transition: color 0.2s; user-select: none;"
                )
                closeBtn.addEventListener("click", {
                    navigationManager.navigateTo(Screen.Landing)
                    rerender()
                })
                card.appendChild(closeBtn)
                overlay.appendChild(card)
                // Click on backdrop (but not card) closes modal
                overlay.addEventListener("click", { e ->
                    if (e.target == overlay) {
                        navigationManager.navigateTo(Screen.Landing)
                        rerender()
                    }
                })
                // Inject the keyframes CSS once
                if (document.getElementById("register-modal-styles") == null) {
                    val style = document.createElement("style") as HTMLElement
                    style.id = "register-modal-styles"
                    style.textContent = """
                        @keyframes modalFadeIn { from { opacity: 0; } to { opacity: 1; } }
                        @keyframes modalScaleIn { from { opacity: 0; transform: scale(0.92) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                    """.trimIndent()
                    document.head?.appendChild(style)
                }
                container.appendChild(overlay)
            }
            is Screen.AccountLinking -> {
                val wrapper = document.createElement("div")
                wrapper.id = "account-linking-container"
                val accountLinkingComponent = AccountLinkingComponent(
                    apiService = apiService,
                    daysRequested = screen.daysRequested,
                    onConnect = { accessToken ->
                        justLinkedPlaid = true  // Prevent redirect back to AccountLinking
                        isDemoMode = false
                        navigationManager.setAccessToken(accessToken)
                        navigationManager.requestTransactionRefresh()
                        // navigateTo changes screen from AccountLinking → TransactionList
                        // and triggers rerender via listener, which calls renderTabContent
                        // with forceRefresh=true to fetch the newly linked account
                        navigationManager.navigateTo(Screen.TransactionList())
                    },
                    onBack = {
                        navigationManager.requestTransactionRefresh()
                        navigationManager.navigateTo(Screen.TransactionList())
                    },
                    onImportStatements = {
                        navigationManager.navigateTo(Screen.StatementImport())
                        rerender()
                    },
                    onSkip = {
                        // Skip never flips demo state. Demo is established at first-time
                        // signup; once the user has moved past it, Skip just navigates back.
                        // (Previously this set isDemoMode = true unconditionally, which wiped
                        //  real linked accounts from the dashboard view.)
                        justLinkedPlaid = true  // Prevent redirect back to AccountLinking
                        navigationManager.navigateTo(Screen.TransactionList())
                        rerender()  // explicit fallback in case navigation listener doesn't fire
                    }
                )
                wrapper.appendChild(accountLinkingComponent.render())
                container.appendChild(wrapper)
            }
            is Screen.StatementImport -> {
                val component = StatementImportComponent(
                    apiService = apiService,
                    appendToAccountId = screen.appendToAccountId,
                    onComplete = { accountId ->
                        // For "create new account" the component returns the
                        // newly-created accountId; for "append" it returns the
                        // same accountId we passed in. Either way, land back
                        // on Transactions for that account.
                        selectedAccountId = accountId
                        justLinkedPlaid = true
                        isDemoMode = false
                        navigationManager.switchToTab(AppTabType.TRANSACTIONS)
                        navigationManager.navigateTo(Screen.TransactionList())
                        rerender()
                    },
                    onBack = {
                        navigationManager.navigateBack()
                        rerender()
                    }
                )
                container.appendChild(component.render())
            }
            else -> {
                // Render main app with tabs
                val mainApp = renderMainApp()
                container.appendChild(mainApp)
            }
        }
        
        return container
    }

    private fun handleLogin(username: String, password: String) {
        if (isLoggingIn) return
        ReadOnlyGate.setDemoMode(false) // leaving any demo session
        isLoggingIn = true
        loginError = null
        clearSessionScopedState()
        rerender() // Re-render to show loading state

        GlobalScope.launch {
            try {
                val response = apiService.login(username, password)

                // Store tokens including expiry time for auto-refresh
                secureStorage.store("app_auth_token", response.token)
                secureStorage.store("app_user_id", response.userId)
                secureStorage.store("app_token_expires_at", response.expiresAtEpochSeconds.toString())

                // CRITICAL: Sync the JWT to the AI auth token store immediately on login
                ml.setAIAuthToken(response.token)

                // CRITICAL: Sync userId to UserIdManager for shared repository access
                // This ensures that SavingsGoalsRepositoryFactory finds the correct ID
                // even if ApiService state is somehow disconnected
                UserIdManager().setUserId(response.userId)
                console.log("✅ WebApp: Synced userId to UserIdManager: ${response.userId}")

                // Block unverified users - require email verification first
                if (!response.emailVerified) {
                    isLoggingIn = false
                    navigationManager.navigateTo(Screen.CheckEmail(username))
                    rerender()
                    return@launch
                }

                // Auto-detect country from browser locale if not already set
                try {
                    val existingCountry = apiService.getSetting("country")
                    if (existingCountry.isNullOrEmpty()) {
                        val browserLocale = kotlinx.browser.window.navigator.language // e.g. "en-CA", "fr-FR"
                        val deviceCountry = browserLocale.substringAfter("-", "").uppercase()
                        if (deviceCountry.isNotEmpty()) {
                            apiService.setSetting("country", deviceCountry)
                            console.log("Auto-detected country: $deviceCountry")
                        }
                    }
                } catch (e: Exception) {
                    console.log("Country auto-detect failed: ${e.message}")
                }

                // Check Plaid status
                checkPlaidStatusAndNavigate()
            } catch (e: MfaRequiredException) {
                // MFA is required - navigate to MFA verification screen
                console.log("MFA required for user: ${e.userId}")
                isLoggingIn = false
                navigationManager.navigateTo(Screen.MfaVerification(e.tempToken, e.userId))
                rerender()
            } catch (e: Exception) {
                console.error("Login failed: ${e.message}")
                loginError = e.message ?: "Login failed"
                isLoggingIn = false
                rerender()
            }
        }
    }

    private fun handleDemoSession() {
        if (isLoggingIn) return
        isLoggingIn = true
        loginError = null
        clearSessionScopedState()
        rerender()

        GlobalScope.launch {
            try {
                val response = apiService.startDemoSession()

                // Demo is intentionally session-only: we do NOT persist the token
                // to secureStorage, so a reload drops the session and the demo
                // "resets". Server-side the token is read-only, so nothing the
                // user does here can touch the shared demo account.
                //
                // NOTE: we deliberately do NOT set isDemoMode — that flag swaps in
                // canned local data. This demo loads the *real* S3 Alex account via
                // the token; ReadOnlyGate.demoMode is what blocks writes + nudges.
                ReadOnlyGate.setDemoMode(true)

                // Point shared user-scoped local reads (goals, etc.) at the demo
                // account WITHOUT clobbering the real user's persisted userId:
                // the in-memory DemoUserOverride takes precedence in
                // UserIdManager.getUserId(). Reset the goals singleton so it
                // rebuilds under the demo identity.
                ml.setAIAuthToken(response.token)
                auth.DemoUserOverride.userId = response.userId
                auth.DemoUserOverride.authToken = response.token
                repository.resetWebSavingsGoalsRepositorySingleton()
                // Survive a reload — changing the language or theme reloads the
                // page, and dropping the visitor to login there reads as being
                // logged out for changing a preference.
                persistDemoSession(response.token, response.userId, response.expiresAtEpochSeconds)

                // The demo account is a Pro account — reflect that so the Pro
                // surfaces (the grid dashboard among them) stay reachable. We
                // skip the live subscription check (it's a POST the demo token
                // can't make anyway).
                //
                // The dashboard opens standard and follows the visitor's own
                // choice. This used to be pinned to "grid", which overwrote the
                // saved preference on every entry AND every reload, so toggling
                // to standard inside the demo never survived — the toggle looked
                // broken. Grid is still one click away and sticks once chosen.
                subscriptionTier = "pro"
                dashboardMode = localStorage.getItem("dashboard_mode") ?: "standard"

                // Land on the Transactions tab — the natural starting point. We
                // skip the Plaid status check (the demo account isn't
                // Plaid-linked and must never be routed to account linking).
                isLoggingIn = false
                // navigateTo() alone, and deliberately not switchToTab() first.
                // switchToTab() assigns currentScreen = Screen.TransactionList()
                // itself, and Screen.TransactionList is a data class, so the
                // navigateTo() that followed matched its own "already on this
                // screen" guard, returned early, and never notified listeners.
                // The session was live but nothing re-rendered, leaving the
                // visitor on the login form until they reloaded — which worked
                // only because persistDemoSession() had already stored it.
                // Reaching this state needs currentTab to have moved off
                // TRANSACTIONS first, which the marketing menu does without a
                // page load. navigateTo() syncs currentTab to TRANSACTIONS on
                // its own, so nothing is lost by dropping the switchToTab().
                //
                // Still exactly one rerender: two would build the Transactions
                // tab twice concurrently and double every startup fetch.
                navigationManager.navigateTo(Screen.TransactionList())
            } catch (e: Exception) {
                console.error("Demo session failed: ${e.message}")
                loginError = e.message ?: "Demo unavailable"
                isLoggingIn = false
                rerender()
            }
        }
    }

    /**
     * Wipe the UI state that belongs to whoever was signed in a moment ago.
     * These are instance fields on a component that survives sign-out, so
     * without this a selection or category filter from the previous session
     * carries into the next one — filtering the new account's list down to
     * zero rows, which reads as "the account is empty".
     */
    /**
     * Re-establish a demo session saved before a reload. Returns false when there
     * is none (or it has expired), leaving the caller to fall through to login.
     *
     * Restores exactly what [handleDemoSession] sets up, so a reloaded demo is
     * indistinguishable from a freshly started one.
     */
    private fun restoreDemoSessionIfAny(): Boolean {
        val token = try { window.sessionStorage.getItem(DEMO_TOKEN_KEY) } catch (_: Exception) { null }
            ?: return false
        val userId = try { window.sessionStorage.getItem(DEMO_USER_ID_KEY) } catch (_: Exception) { null }
            ?: return false
        val expiresAt = try {
            window.sessionStorage.getItem(DEMO_EXPIRES_AT_KEY)?.toLongOrNull() ?: 0L
        } catch (_: Exception) { 0L }

        if (expiresAt > 0 && (kotlin.js.Date.now() / 1000).toLong() > expiresAt) {
            console.log("Demo session expired, clearing")
            clearPersistedDemoSession()
            return false
        }

        apiService.restoreSession(token, userId, expiresAt)
        ml.setAIAuthToken(token)
        ReadOnlyGate.setDemoMode(true)
        auth.DemoUserOverride.userId = userId
        auth.DemoUserOverride.authToken = token
        repository.resetWebSavingsGoalsRepositorySingleton()
        // Same Pro-account framing the fresh demo sets up, and the same
        // deference to a saved dashboard choice — pinning "grid" here was half
        // of why the in-demo toggle never stuck across a reload.
        subscriptionTier = "pro"
        dashboardMode = localStorage.getItem("dashboard_mode") ?: "standard"
        console.log("Restored demo session after reload")
        return true
    }

    /**
     * Raise the no-account demo notice — nothing the visitor does here is saved —
     * and retire it after [demoNoticeMs].
     *
     * Driven by navigation rather than by [render], and parented to `document.body`
     * rather than to the app container, because neither of those could carry it:
     *
     *  - It began life as the first child of mainContainer, in the document flow.
     *    That made it correct for exactly one frame — rendering a tab restores the
     *    previous scroll position (see pendingScrollRestore), which carried it up
     *    out of the viewport. It read as a flicker rather than as a notice.
     *  - Raising it from [render] then missed the case that matters most: a tab
     *    switch calls renderTabContent directly, never [render], so clicking into
     *    a tab — the commonest way to reach a new page — raised nothing at all.
     *
     * On `document.body` it neither disturbs the app's layout nor depends on it,
     * and a rerender can neither duplicate nor destroy it.
     */
    private fun raiseDemoNotice() {
        // Whatever is on screen belongs to the page being left.
        demoNoticeTimer?.let { window.clearTimeout(it) }
        demoNoticeTimer = null
        document.getElementById(DEMO_NOTICE_ID)?.let { it.parentElement?.removeChild(it) }
        if (!ReadOnlyGate.demoMode) return

        val notice = (document.createElement("div") as HTMLElement).apply {
            id = DEMO_NOTICE_ID
            setAttribute("role", "status")
            // Above the components' sticky headers (≤2000), below modals
            // (≥10000) — a dialog must still cover it.
            style.cssText = """
                position:fixed; top:0; left:0; right:0; z-index:3000;
                display:flex; align-items:center; justify-content:center; gap:14px;
                background:linear-gradient(90deg,#0f2b33,#0a1f26); color:#e6feff;
                padding:9px 16px; font-size:13px; font-weight:500; text-align:center;
                border-bottom:1px solid rgba(0,217,255,0.25);
                opacity:1; transition:opacity 0.4s ease;
            """
            innerHTML = """
                <span style="display:inline-flex; align-items:flex-start; gap:6px; text-align:left;"><span>✨</span><span>${i18n.StringKey.DEMO_BANNER.tr()}</span></span>
                <button id="demo-banner-cta" style="
                    background:#00d9ff; color:#0a0a0a; border:none; border-radius:6px;
                    padding:5px 12px; font-size:12px; font-weight:600; cursor:pointer; white-space:nowrap;
                ">${i18n.StringKey.AUTH_CREATE_ACCOUNT.tr()}</button>
            """.trimIndent()
        }
        notice.querySelector("#demo-banner-cta")?.addEventListener("click", {
            navigationManager.navigateTo(Screen.Register)
            rerender()
        })
        document.body?.appendChild(notice)

        demoNoticeTimer = window.setTimeout({
            notice.style.opacity = "0"
            // Detach after the fade: left at opacity 0 it would go on swallowing
            // clicks meant for the header beneath it.
            window.setTimeout({ notice.parentElement?.removeChild(notice) }, 400)
        }, demoNoticeMs)
    }

    private fun clearSessionScopedState() {
        selectedAccountId = null
        stickyTransactionCategoryFilter = null
        accounts = emptyList()
        cachedTransactions = emptyList()
    }

    private fun handleMfaVerification(tempToken: String, code: String) {
        if (isLoggingIn) return
        isLoggingIn = true
        loginError = null
        rerender()

        GlobalScope.launch {
            try {
                val response = apiService.verifyMfaLogin(tempToken, code)

                // Store tokens including expiry time for auto-refresh
                secureStorage.store("app_auth_token", response.token)
                secureStorage.store("app_user_id", response.userId)
                secureStorage.store("app_token_expires_at", response.expiresAtEpochSeconds.toString())

                // CRITICAL: Sync the JWT to the AI auth token store immediately on MFA login
                ml.setAIAuthToken(response.token)

                // Sync userId to UserIdManager
                UserIdManager().setUserId(response.userId)
                console.log("✅ WebApp: MFA verified, synced userId: ${response.userId}")

                // Check Plaid status
                checkPlaidStatusAndNavigate()
            } catch (e: Exception) {
                console.error("MFA verification failed: ${e.message}")
                loginError = e.message ?: "Invalid MFA code"
                isLoggingIn = false
                rerender()
            }
        }
    }

    private fun handleRegisterWithData(registrationData: RegistrationData) {
        if (isLoggingIn) return
        isLoggingIn = true
        loginError = null
        rerender()

        GlobalScope.launch {
            try {
                // Register using email as username, and pass email for whitelist validation
                apiService.register(registrationData.email, registrationData.password, registrationData.email)

                // Store additional user info locally for now
                // TODO: Add API endpoint to store user profile data
                secureStorage.store("user_first_name", registrationData.firstName)
                secureStorage.store("user_last_name", registrationData.lastName)
                secureStorage.store("user_postal_code", registrationData.postalCode)
                registrationData.phone?.let { secureStorage.store("user_phone", it) }
                registrationData.dateOfBirth?.let { secureStorage.store("user_dob", it) }

                // Don't auto-login - require email verification first
                navigationManager.navigateTo(Screen.CheckEmail(registrationData.email))
                isLoggingIn = false
                rerender()
            } catch (e: Exception) {
                console.error("Registration failed: ${e.message}")
                loginError = e.message ?: "Registration failed"
                isLoggingIn = false
                rerender()
            }
        }
    }

    private suspend fun checkPlaidStatusAndNavigate() {
        try {
            // If user just linked Plaid, skip the check and go to Activity
            if (justLinkedPlaid) {
                console.log("✅ User just linked Plaid, skipping status check and going to Activity")
                navigationManager.switchToTab(AppTabType.TRANSACTIONS)
                navigationManager.navigateTo(Screen.TransactionList())
                return
            }

            val linkStatus = apiService.getPlaidLinkStatus()
            if (linkStatus.has_plaid_token) {
                // Go to Activity (Transactions) tab after login
                navigationManager.switchToTab(AppTabType.TRANSACTIONS)
                navigationManager.navigateTo(Screen.TransactionList())
            } else {
                navigationManager.navigateTo(Screen.AccountLinking(daysRequested = 90))
            }
        } catch (e: Exception) {
            console.error("Failed to check Plaid status: ${e.message}")
            // Default to Activity if check fails, assuming user might be linked
            navigationManager.switchToTab(AppTabType.TRANSACTIONS)
            navigationManager.navigateTo(Screen.TransactionList())
        } finally {
            isLoggingIn = false
            rerender() // Must rerender after navigation completes
        }
    }

    private fun renderTransactionDetail(screen: Screen.TransactionDetail, container: HTMLElement, originalTransaction: Transaction? = null, closeAction: ((Boolean) -> Unit)? = null) {
        container.innerHTML = ""

        // Use original Transaction if provided (preserves all fields including contextIds),
        // otherwise reconstruct from TransactionData (which may lose newer fields)
        val transaction =
            if (originalTransaction != null) {
                originalTransaction
            } else if (screen.transactionData != null) {
                val data = screen.transactionData!!
                // Parse date string as local time (not UTC) to avoid date shift
                val dateParts = data.date.split("-")
                val year = dateParts[0].toInt()
                val month = dateParts[1].toInt() - 1 // JavaScript months are 0-based
                val day = dateParts[2].toInt()
                val localDate = kotlin.js.Date(year, month, day)
                Transaction(
                    id = data.id,
                    merchant = data.merchant,
                    amount = data.amount,
                    date = localDate,
                    category = data.category,
                    account = data.account,
                    isDebit = data.isDebit,
                    aiConfidence = data.aiConfidence,
                    categorySource = data.categorySource,
                    categoryLevel1 = data.categoryLevel1,
                    categoryLevel2 = data.categoryLevel2,
                    categoryLevel3 = data.categoryLevel3,
                    confidenceLevel = data.confidenceLevel,
                    // Goal assignment fields
                    assignedGoalId = data.assignedGoalId,
                    assignedGoalName = data.assignedGoalName,
                    assignedGoalIcon = data.assignedGoalIcon,
                    // Split/Distribute fields
                    isSplitParent = data.isSplitParent,
                    isDistributedParent = data.isDistributedParent,
                    parentTransactionId = data.parentTransactionId,
                    childType = data.childType,
                    distributionStartMonth = data.distributionStartMonth,
                    distributionEndMonth = data.distributionEndMonth,
                    // User notes
                    userNotes = data.userNotes,
                    isExcluded = data.isExcluded,
                )
            } else {
                navigationManager.getSelectedTransaction() ?: Transaction(
                    id = screen.transactionId,
                    merchant = "Transaction Not Found",
                    amount = 0.0,
                    date = kotlin.js.Date(),
                    category = "Unknown",
                    account = "Unknown Account",
                    isDebit = true,
                )
            }

        fun createSplitHandler(currentTransaction: Transaction): (List<SplitItemData>) -> Unit =
            { splits ->
                kotlinx.coroutines.MainScope().launch {
                    try {
                        val splitRequests = splits.map { split ->
                            SplitItemRequest(
                                amount = split.amount,
                                category = split.primaryCategory,
                                detailedCategory = split.subCategory.ifEmpty { null },
                                note = split.note.ifEmpty { null }
                            )
                        }
                        val result = apiService.splitTransaction(currentTransaction.id, splitRequests)
                        console.log("Split transaction successful: ${result.childTransactionIds.size} children created")
                        // Request refresh and close/navigate back after successful split
                        closeAction?.invoke(true) ?: run {
                            navigationManager.requestTransactionRefresh()
                            navigationManager.navigateBack()
                        }
                    } catch (e: Exception) {
                        console.error("Failed to split transaction:", e.message)
                    }
                }
            }

        fun createUnsplitHandler(currentTransaction: Transaction): () -> Unit =
            {
                kotlinx.coroutines.MainScope().launch {
                    try {
                        // If this is a child, send the parent's ID to undo the split/distribute
                        val targetId = currentTransaction.parentTransactionId ?: currentTransaction.id
                        apiService.deleteSplit(targetId)
                        console.log("Split/distribute removed for transaction $targetId")
                        // Request refresh and close/navigate back after successful unsplit
                        closeAction?.invoke(true) ?: run {
                            navigationManager.requestTransactionRefresh()
                            navigationManager.navigateBack()
                        }
                    } catch (e: Exception) {
                        console.error("Failed to unsplit transaction:", e.message)
                        showAppAlert(e.message ?: "Failed to undo split/distribute")
                    }
                }
            }

        fun createUnsplitGrandparentHandler(currentTransaction: Transaction): () -> Unit =
            {
                kotlinx.coroutines.MainScope().launch {
                    try {
                        // For distributed-of-split children, extract the original parent ID
                        // Parent is a split child: "{originalId}_split_{index}"
                        val parentId = currentTransaction.parentTransactionId ?: currentTransaction.id
                        val grandparentId = if (parentId.contains("_split_")) {
                            parentId.substringBefore("_split_")
                        } else {
                            parentId
                        }
                        apiService.deleteSplit(grandparentId)
                        console.log("Unsplit grandparent $grandparentId (from child ${currentTransaction.id})")
                        closeAction?.invoke(true) ?: run {
                            navigationManager.requestTransactionRefresh()
                            navigationManager.navigateBack()
                        }
                    } catch (e: Exception) {
                        console.error("Failed to unsplit grandparent:", e.message)
                        showAppAlert(e.message ?: "Failed to unsplit")
                    }
                }
            }

        fun createUndistributeHandler(currentTransaction: Transaction): () -> Unit =
            {
                kotlinx.coroutines.MainScope().launch {
                    try {
                        // For split-of-distributed children, extract the original parent ID from the distributed child's ID
                        // Distributed child IDs follow pattern: "{originalId}_dist_{YYYY-MM}"
                        val parentId = currentTransaction.parentTransactionId ?: currentTransaction.id
                        val grandparentId = if (parentId.contains("_dist_")) {
                            parentId.substringBefore("_dist_")
                        } else {
                            parentId
                        }
                        apiService.deleteSplit(grandparentId)
                        console.log("Undistributed transaction $grandparentId (from child ${currentTransaction.id})")
                        closeAction?.invoke(true) ?: run {
                            navigationManager.requestTransactionRefresh()
                            navigationManager.navigateBack()
                        }
                    } catch (e: Exception) {
                        console.error("Failed to undistribute:", e.message)
                        showAppAlert(e.message ?: "Failed to undistribute")
                    }
                }
            }

        fun createDistributeHandler(currentTransaction: Transaction): (String, String) -> Unit =
            { startMonth, endMonth ->
                kotlinx.coroutines.MainScope().launch {
                    try {
                        val result = apiService.distributeTransaction(currentTransaction.id, startMonth, endMonth)
                        console.log("Distributed transaction: ${result.childTransactionIds.size} months created")
                        // Close modal and refresh list — same pattern as split
                        closeAction?.invoke(true) ?: run {
                            navigationManager.requestTransactionRefresh()
                            navigationManager.navigateBack()
                        }
                    } catch (e: Exception) {
                        console.error("Failed to distribute transaction: ${e.message}")
                    }
                }
            }


        fun createCategoryChangeHandler(currentTransaction: Transaction): (String, Boolean, List<String>?) -> Unit =
            { newCategory, applyToSimilar, selectedTransactionIds ->
                kotlinx.coroutines.MainScope().launch {
                    try {
                        // Call API to persist the category change
                        apiService.updateTransactionCategory(
                            currentTransaction.id,
                            newCategory,
                            applyToSimilar,
                            selectedTransactionIds,
                        )
                        console.log("Category updated for transaction ${currentTransaction.id}: ${currentTransaction.category} -> $newCategory")

                        // Update ML model with user correction
                        correctTransactionCategory(
                            originalCategory = currentTransaction.category,
                            correctedCategory = newCategory,
                            merchantName = currentTransaction.merchant,
                            confidence = currentTransaction.aiConfidence?.toDouble() ?: 0.0,
                        )

                        val parts = newCategory.split("/")
                        val displayText = if (parts.size >= 2) {
                            utils.CategoryUtils.formatTwoLevelCategoryLocalized(parts[0], parts[1])
                        } else {
                            utils.CategoryUtils.formatCategoryNameLocalized(newCategory)
                        }

                        // Update the detail overlay category display
                        kotlinx.browser.document.getElementById("txn-detail-category-display")?.textContent = displayText

                        // Update the detail overlay icon (border color + SVG). Pass the user
                        // colour overrides so custom primaries ("Extra" etc.) don't fall to grey.
                        val newColor = utils.CategoryUtils.getCategoryColor(newCategory, userCategoryColors)
                        val newIconId = utils.CategoryUtils.getCategoryIconId(newCategory)
                        // Prefer a user-picked icon when the (primary, sub) pair matches a custom row.
                        val codeParts = newCategory.split("/")
                        val userIconSvgDetail = findCustomIconId(
                            customCategories,
                            codeParts.getOrNull(0).orEmpty(),
                            codeParts.getOrNull(1),
                        )?.let { utils.CustomCategoryIcons.renderSvg(it, size = 22) }
                        (kotlinx.browser.document.getElementById("txn-detail-icon") as? org.w3c.dom.HTMLElement)?.let { iconEl ->
                            iconEl.style.borderColor = newColor
                            iconEl.style.color = WebTheme.current.iconStroke
                            iconEl.innerHTML = userIconSvgDetail ?: CategoryIconSvg.render(newIconId, size = 22)
                        }

                        // Build the verified "Me: 100%" badge HTML once
                        val userInfo = utils.CategoryUtils.getConfidenceInfo(
                            categorySource = "user",
                            confidenceLevel = null,
                            isVerified = true
                        )
                        val badgeColor = "#1976D2"
                        val verifiedBadgeHtml = "<span style=\"font-size:12px;color:#4CAF50;font-weight:bold;\">\u2713</span><span style=\"font-size:10px;font-weight:500;color:$badgeColor;background:${badgeColor}22;padding:2px 4px;border-radius:4px;white-space:nowrap;\">${userInfo.label}</span>"

                        // Helper to update a list row's icon. Prefers user-picked icon when
                        // the (primary, sub) pair matches a custom row.
                        val userIconSvgList = findCustomIconId(
                            customCategories,
                            codeParts.getOrNull(0).orEmpty(),
                            codeParts.getOrNull(1),
                        )?.let { utils.CustomCategoryIcons.renderSvg(it, size = 18) }
                        fun updateRowIcon(txId: String) {
                            (kotlinx.browser.document.getElementById("txn-icon-$txId") as? org.w3c.dom.HTMLElement)?.let { iconEl ->
                                iconEl.style.borderColor = newColor
                                iconEl.style.color = WebTheme.current.iconStroke
                                iconEl.innerHTML = userIconSvgList ?: CategoryIconSvg.render(newIconId, size = 18)
                            }
                        }

                        // Helper: patch a single row in the visible list to reflect the new category
                        fun patchRow(txId: String) {
                            (kotlinx.browser.document.getElementById("txn-category-$txId") as? org.w3c.dom.HTMLElement)?.let { catEl ->
                                catEl.textContent = displayText
                                catEl.style.color = "#666"
                            }
                            kotlinx.browser.document.getElementById("verify-badge-$txId")?.let { badgeEl ->
                                badgeEl.innerHTML = verifiedBadgeHtml
                            }
                            updateRowIcon(txId)
                        }

                        if (selectedTransactionIds != null) {
                            // Per-row checkbox flow: update exactly the IDs the user picked.
                            // The source itself is included in the set (it's locked-checked
                            // in the preview), so this also covers the source row.
                            selectedTransactionIds.forEach { patchRow(it) }
                            closeAction?.invoke(false) ?: navigationManager.navigateBack()
                        } else if (applyToSimilar) {
                            // Legacy server-side merchant+category match. Patch all visible
                            // rows with the matching merchant + original category.
                            val originalCategory = currentTransaction.category
                            val nodes = kotlinx.browser.document.querySelectorAll("[data-merchant]")
                            for (i in 0 until nodes.length) {
                                val htmlEl = nodes.item(i) as? org.w3c.dom.HTMLElement ?: continue
                                val txMerchant = htmlEl.getAttribute("data-merchant") ?: continue
                                val txCategory = htmlEl.getAttribute("data-category") ?: continue
                                if (txMerchant == currentTransaction.merchant && txCategory == originalCategory) {
                                    val txId = htmlEl.getAttribute("data-txn-id") ?: continue
                                    patchRow(txId)
                                }
                            }
                            closeAction?.invoke(false) ?: navigationManager.navigateBack()
                        } else {
                            // Single-transaction update: patch this row in-place.
                            patchRow(currentTransaction.id)
                        }
                    } catch (e: Exception) {
                        if (e.message == "SUBSCRIPTION_REQUIRED") {
                            console.log("🚫 Category update blocked: subscription required")
                            // Show paywall modal on top of the existing detail popup
                            // so the user sees the upgrade pitch in context. Don't
                            // close the detail view — the user can dismiss the modal
                            // and keep their place.
                            showSubscribeModal()
                        } else {
                            console.error("Failed to update category:", e.message)
                        }
                    }
                }
            }

        val goalsRepository = createSavingsGoalsRepositoryWithAuth(
            getAuthToken = { ApiService.getSharedAuthToken() ?: "" }
        )
        val transactionDetailComponent =
            TransactionDetailComponent(
                transaction = transaction,
                onBackClick = {
                    closeAction?.invoke(false) ?: navigationManager.navigateBack()
                },
                onCategoryChanged = createCategoryChangeHandler(transaction),
                onSplitTransaction = createSplitHandler(transaction),
                onUnsplitTransaction = createUnsplitHandler(transaction),
                onUndistributeTransaction = createUndistributeHandler(transaction),
                onUnsplitGrandparent = createUnsplitGrandparentHandler(transaction),
                onDistributeTransaction = createDistributeHandler(transaction),
                onGoalAssigned = { goal ->
                    console.log("Transaction assigned to goal: ${goal.name}")
                    closeAction?.invoke(true) ?: run {
                        navigationManager.requestTransactionRefresh()
                        navigationManager.navigateBack()
                    }
                },
                onGoalRemoved = {
                    console.log("Goal assignment removed")
                    closeAction?.invoke(true) ?: run {
                        navigationManager.requestTransactionRefresh()
                        navigationManager.navigateBack()
                    }
                },
                goalsRepository = goalsRepository,
                apiService = apiService,
                isReadOnly = isReadOnly,
                isProTier = isProTier,
                isTrialing = isTrialing,
                onSubscribeClick = {
                    // The transaction detail is rendered as a fixed-position
                    // overlay on top of the main content; if we just navigate,
                    // the Subscribe screen mounts UNDERNEATH the still-open
                    // overlay. Close the overlay first, then navigate so the
                    // paywall actually surfaces.
                    closeAction?.invoke(false)
                    navigationManager.navigateTo(Screen.Subscribe(proOnly = true))
                },
                onExcludeChanged = { isExcluded ->
                    GlobalScope.launch {
                        try {
                            if (isExcluded) {
                                apiService.markExcluded(transaction.id)
                            } else {
                                apiService.unmarkExcluded(transaction.id)
                            }
                            closeAction?.invoke(true) ?: run {
                                navigationManager.requestTransactionRefresh()
                                navigationManager.navigateBack()
                            }
                        } catch (e: Exception) {
                            console.error("Failed to update exclude status: ${e.message}")
                        }
                    }
                },
                onContextChanged = {
                    // Close detail and refresh transaction list with fresh context_ids
                    closeAction?.invoke(true) ?: run {
                        navigationManager.requestTransactionRefresh()
                        navigationManager.navigateBack()
                    }
                },
                onCategoryColorChanged = {
                    // Silently reload color overrides + custom categories so icon-render
                    // sites (list, detail header, DOM patches) see the new icon/color.
                    GlobalScope.launch {
                        try {
                            val colorJson = apiService.getSetting("custom_category_colors")
                            if (!colorJson.isNullOrBlank()) {
                                userCategoryColors = kotlinx.serialization.json.Json.decodeFromString(colorJson)
                            }
                        } catch (_: Exception) {}
                        try {
                            customCategories = apiService.getCustomCategories()
                        } catch (_: Exception) {}
                    }
                    // Deleting/renaming a custom category can rewrite personal_finance_category_*
                    // on affected transactions. Ask the list for fresh data so those rows update.
                    navigationManager.requestTransactionRefresh()
                },
                onNavigateToParent = if (!transaction.parentTransactionId.isNullOrEmpty()) {
                    {
                        // Close current modal and open parent's detail
                        val parentId = transaction.parentTransactionId!!
                        closeAction?.invoke(false)
                        // Find parent in cached transactions list and open its detail
                        val parentTx = cachedTransactions.find { it.id == parentId }
                        if (parentTx != null) {
                            showTransactionDetailModal(parentTx)
                        }
                    }
                } else null,
                parentDate = if (!transaction.parentTransactionId.isNullOrEmpty()) {
                    cachedTransactions.find { it.id == transaction.parentTransactionId }?.let { parent ->
                        val monthNames = arrayOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec")
                        "${monthNames[parent.date.getMonth()]} ${parent.date.getDate()}, ${parent.date.getFullYear()}"
                    }
                } else null,
                parentAmount = if (!transaction.parentTransactionId.isNullOrEmpty()) {
                    cachedTransactions.find { it.id == transaction.parentTransactionId }?.amount
                } else null,
                userCategoryColors = userCategoryColors,
                initialCustomCategories = customCategories,
            )
        container.appendChild(transactionDetailComponent.render())
    }

    private fun buildSavingsRecommendationHtml(
        monthlyAvailable: Double,
        goals: List<models.SavingsGoal>,
        monthLabel: String,
        hasMonthData: Boolean = true,
        hasIncomeYet: Boolean = true,
    ): String {
        val dollarSign = "$"
        val tc = WebTheme.current

        // Web-side greedy allocation split, computed up front so the shared state
        // machine can tell whether there's a breakdown to show.
        val allocationItems: List<String> = if (monthlyAvailable > 0 && goals.isNotEmpty()) {
            var remaining = monthlyAvailable
            goals.sortedWith(compareBy(
                { when (it.priority) {
                    models.GoalPriority.MUST_HAVE -> 0; models.GoalPriority.NICE_TO_HAVE -> 1
                    models.GoalPriority.STRETCH_GOAL -> 2; models.GoalPriority.MAYBE -> 3; else -> 4
                }},
                { it.getDaysRemaining() }
            )).take(5).mapNotNull { goal ->
                val daysLeft = maxOf(1, goal.getDaysRemaining())
                // Shared definition — see analytics.goalMonthsRemaining.
                val monthsLeft = maxOf(1, analytics.goalMonthsRemaining(goal))
                val needed = analytics.computeGoalPace(goal).requiredMonthly
                val allocation = minOf(needed, remaining).coerceAtLeast(0.0)
                if (allocation > 0) {
                    remaining -= allocation
                    val timeText = if (daysLeft < 30) "${i18n.StringKey.DASHBOARD_TIME_DAYS_LEFT.tr(daysLeft.toString())}" else "${i18n.StringKey.DASHBOARD_TIME_MONTHS_LEFT.tr(monthsLeft.toString())}"
                    "• <span class='goal-link' data-goal-id='${goal.id}' style='color: ${WebTheme.current.accent}; cursor: pointer; text-decoration: underline;'>${CategoryIconSvg.renderGoalIcon(goal.iconEmoji, size = 14, color = WebTheme.current.accent)} ${goal.name}</span> – $dollarSign${allocation.toInt()} ($timeText)"
                } else null
            }
        } else emptyList()

        // Single source of truth for WHICH card to show — every renderer routes
        // through this so the decision can't drift again (that split was the
        // "over budget when income just hasn't landed" bug).
        val state = recommendations.computeSavingsRecommendationCardState(
            hasGoals = goals.isNotEmpty(),
            hasMonthData = hasMonthData,
            hasIncomeYet = hasIncomeYet,
            actualSavings = monthlyAvailable,
            hasAllocations = allocationItems.isNotEmpty(),
            // This surface has no month stepper: it always shows the last COMPLETE
            // month, which by definition is not still running.
            monthInProgress = false,
        )

        // Headline — only when the state allows it (suppressed for NoGoals and
        // ExpensesBeforeIncome, where the figure would mislead).
        val availableText = if (state.showsAvailableAmount) {
            when {
                monthlyAvailable > 0 -> i18n.StringKey.DASHBOARD_AVAILABLE_THIS_MONTH.tr("<span style='color: ${tc.incomeGreen}; font-weight: 600;'>${dollarSign}${monthlyAvailable.toInt()}</span>", monthLabel)
                monthlyAvailable < 0 -> i18n.StringKey.DASHBOARD_OVER_BUDGET_THIS_MONTH.tr("<span style='color: ${tc.expenseRed}; font-weight: 600;'>${dollarSign}${kotlin.math.abs(monthlyAvailable).toInt()}</span>", monthLabel)
                else -> ""
            }
        } else ""

        // A hint/nudge/list follows the headline; when the headline is suppressed
        // it stands alone (no leading spacer).
        fun withSpacer(text: String) = if (availableText.isEmpty()) text else "<br/><br/>$text"
        fun ctaButton(id: String, label: String) = """
            <button id="$id" style="
                margin-top: 4px;
                padding: 8px 16px;
                border: 1px solid ${tc.accent};
                border-radius: 6px;
                background: transparent;
                color: ${tc.accent};
                cursor: pointer;
                font-size: 13px;
                font-weight: 500;
                transition: background 0.2s;
            " onmouseover="this.style.background='rgba(0,217,255,0.1)'" onmouseout="this.style.background='transparent'">$label</button>
        """.trimIndent()

        val body = availableText + when (state) {
            is recommendations.SavingsRecommendationCardState.NoGoals ->
                withSpacer(i18n.StringKey.DASHBOARD_GOAL_SUGGESTION.tr()) + "<br/><br/>" + ctaButton("recommendationCreateGoalBtn", i18n.StringKey.DASHBOARD_CREATE_GOAL.tr())
            is recommendations.SavingsRecommendationCardState.EarlyMonth ->
                withSpacer(i18n.StringKey.DASHBOARD_EARLY_MONTH_HINT.tr())
            recommendations.SavingsRecommendationCardState.NoMonthData ->
                withSpacer(i18n.StringKey.DASHBOARD_NO_MONTH_DATA.tr(monthLabel))
            is recommendations.SavingsRecommendationCardState.ExpensesBeforeIncome ->
                withSpacer(i18n.StringKey.DASHBOARD_EXPENSES_BEFORE_INCOME.tr())
            is recommendations.SavingsRecommendationCardState.HasAllocations ->
                "<br/><br/>${i18n.StringKey.DASHBOARD_RECOMMENDED_ALLOCATION_HEADER.tr()}<br/>${allocationItems.joinToString("<br/>")}"
            is recommendations.SavingsRecommendationCardState.SpendLessNudge ->
                // Surplus but nothing left to allocate (goals funded) → just the
                // available line, no "spend less" nudge. Otherwise nudge + CTA.
                if (monthlyAvailable > 0) ""
                else withSpacer(i18n.StringKey.DASHBOARD_NUDGE_SPEND_LESS.tr(monthLabel)) + "<br/><br/>" + ctaButton("recommendationViewBudgetBtn", i18n.StringKey.DASHBOARD_VIEW_BUDGET.tr())
        }

        val t = WebTheme.current
        val bulbCircleBg = if (t.isDark) "rgba(0,217,255,0.22)" else "rgba(0,151,167,0.22)"
        return """
            <div id="recommendationPanel" style="background: ${t.card}; border-radius: 12px; padding: 16px 20px; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};">
                <div style="display: flex; align-items: flex-start; gap: 12px;">
                    <div style="width: 36px; height: 36px; background: $bulbCircleBg; border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0;">
                        <span style="font-size: 18px;">💡</span>
                    </div>
                    <div style="flex: 1;">
                        <h3 style="color: ${t.accent}; margin: 0 0 8px 0; font-size: 13px; font-weight: 600;">${i18n.StringKey.DASHBOARD_SAVINGS_RECOMMENDATION.tr()}</h3>
                        <div style="color: ${t.textSecondary}; margin: 0; font-size: 12px; line-height: 1.6;">$body</div>
                    </div>
                </div>
            </div>
        """
    }

    private fun showTransactionDetailModal(transaction: Transaction) {
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.style.cssText = """
            position: fixed;
            inset: 0;
            background: rgba(0, 0, 0, 0.72);
            z-index: 1000;
            display: flex;
            align-items: flex-start;
            justify-content: center;
            overflow-y: auto;
            padding: 24px 16px;
            box-sizing: border-box;
        """.trimIndent()

        val panel = document.createElement("div") as HTMLElement
        // Panel background sits a touch darker than t.background so cards read
        // clearly on top of it; the note card below mirrors this same colour.
        val panelBg = "color-mix(in srgb, ${WebTheme.current.background}, ${if (WebTheme.current.isDark) "black 20%" else "black 7%"})"
        panel.style.cssText = """
            background: $panelBg;
            border-radius: 20px;
            width: 100%;
            max-width: 640px;
            flex-shrink: 0;
            overflow: hidden;
            margin-bottom: 24px;
        """.trimIndent()

        var modalClosed = false

        fun closeModal(refresh: Boolean) {
            if (modalClosed) return
            modalClosed = true
            backdrop.remove()
            val pendingRefresh = navigationManager.consumeTransactionRefresh()
            if (refresh || pendingRefresh) {
                // Save scroll position so the transaction list restores to the same spot
                pendingScrollRestore = window.scrollY
                renderTabContent(navigationManager.getCurrentTab())
            }
        }

        // ESC key closes modal
        kotlinx.browser.document.addEventListener("keydown", { event ->
            if (!modalClosed && event.asDynamic().key == "Escape") closeModal(false)
        })

        // Click outside panel (on backdrop) closes modal
        backdrop.addEventListener("click", { event ->
            if (event.target == backdrop) closeModal(false)
        })

        // Clicks inside panel don't bubble to backdrop
        panel.addEventListener("click", { event ->
            event.stopPropagation()
        })

        backdrop.appendChild(panel)
        document.body?.appendChild(backdrop)

        // Build date string YYYY-MM-DD from JS Date
        val y = transaction.date.getFullYear()
        val m = (transaction.date.getMonth() + 1).toString().padStart(2, '0')
        val d = transaction.date.getDate().toString().padStart(2, '0')

        val screen = Screen.TransactionDetail(
            transactionId = transaction.id,
            transactionData = TransactionData(
                id = transaction.id,
                merchant = transaction.merchant,
                amount = transaction.amount,
                date = "$y-$m-$d",
                category = transaction.category,
                account = transaction.account,
                isDebit = transaction.isDebit,
                aiConfidence = transaction.aiConfidence,
                categorySource = transaction.categorySource,
                confidenceLevel = transaction.confidenceLevel,
                categoryLevel1 = transaction.categoryLevel1,
                categoryLevel2 = transaction.categoryLevel2,
                categoryLevel3 = transaction.categoryLevel3,
                assignedGoalId = transaction.assignedGoalId,
                assignedGoalName = transaction.assignedGoalName,
                assignedGoalIcon = transaction.assignedGoalIcon,
                isSplitParent = transaction.isSplitParent,
                isDistributedParent = transaction.isDistributedParent,
                parentTransactionId = transaction.parentTransactionId,
                childType = transaction.childType,
                distributionStartMonth = transaction.distributionStartMonth,
                distributionEndMonth = transaction.distributionEndMonth,
                userNotes = transaction.userNotes,
                isExcluded = transaction.isExcluded,
            )
        )

        renderTransactionDetail(screen, panel, transaction) { refresh -> closeModal(refresh) }
    }

    private fun renderMainApp(): HTMLElement {
        val mainContainer = document.createElement("div") as HTMLElement
        mainContainer.className = "main-app-container"
        mainContainer.style.cssText = """
            display: flex;
            flex-direction: column;
            min-height: 100dvh;
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        """

        // Header
        // Header removed - navigation is now in individual components

        // Tab Navigation - hidden, navigation is in components
        val tabNav = document.createElement("div") as HTMLElement
        tabNav.style.display = "none"

        // Content Area
        contentElement = document.createElement("div") as HTMLElement
        contentElement!!.className = "tab-content"
        contentElement!!.style.cssText = """
            flex: 1;
            background: transparent;
        """

        // Set up tab listener (only once to prevent accumulation on rerenders)
        if (!tabListenerAdded) {
            tabListenerAdded = true
            navigationManager.addTabListener { tab ->
                // Reset account selection when switching tabs (except when staying on transactions)
                // Keep account selection when switching tabs
                // if (tab != AppTabType.TRANSACTIONS) {
                //    selectedAccountId = null
                // }
                renderTabContent(tab)
                // A tab switch never reaches render(), so the notice has to be
                // raised from here — this is the commonest way into a new page.
                raiseDemoNotice()
            }
        }

        // Subscription/trial status banner — shown above all tab content
        val banner = SubscriptionBanner(
            onSubscribeClick = { navigationManager.navigateTo(Screen.Subscribe()) }
        ).render()
        mainContainer.appendChild(banner)

        // Offline banner — shown when network is lost
        val offlineBanner = (kotlinx.browser.document.createElement("div") as HTMLElement).apply {
            id = "offline-banner"
            style.cssText = "display:none; background:#B33333; color:white; padding:10px 16px; font-size:13px; font-weight:500; text-align:center;"
            textContent = i18n.StringKey.OFFLINE_BANNER.tr()
        }
        mainContainer.appendChild(offlineBanner)
        NetworkMonitor.addListener { offline ->
            offlineBanner.style.display = if (offline) "block" else "none"
            if (!offline) {
                // Back online — clear stale containers and refresh current tab
                tabContainers.values.forEach { it.parentNode?.removeChild(it) }
                tabContainers.clear()
                activeTabContainer = null
                renderTabContent(navigationManager.getCurrentTab())
            }
        }
        if (NetworkMonitor.isOffline) offlineBanner.style.display = "block"

        mainContainer.appendChild(contentElement!!)

        // Add hamburger menu overlay for desktop app pages
        if (!ResponsiveUtils.isMobile()) {
            val menuDiv = document.createElement("div") as HTMLElement
            menuDiv.innerHTML = MarketingMenu.overlayHtml(currentPage = "app", isLoggedIn = true)
            mainContainer.appendChild(menuDiv)

            // Use event delegation: any click on #nav-settings opens the menu
            mainContainer.addEventListener("click", { e ->
                val target = e.target as? HTMLElement ?: return@addEventListener
                // Don't intercept clicks inside the menu overlay itself
                val overlay = document.getElementById("marketing-menu-overlay") as? HTMLElement
                if (overlay != null && overlay.contains(target)) return@addEventListener

                // Walk up to find #nav-settings
                var el: HTMLElement? = target
                while (el != null && el != mainContainer) {
                    if (el.id == "nav-settings" || el.id == "nav-settings-hamburger") {
                        e.preventDefault()
                        e.stopPropagation()
                        overlay?.style?.display = "block"
                        return@addEventListener
                    }
                    el = el.parentElement as? HTMLElement
                }
            })

            window.setTimeout({
                MarketingMenu.attachEventListeners(
                    onNavigate = ::menuNavigate,
                    isLoggedIn = true,
                    onAppNavigate = ::menuAppNavigate,
                )
            }, 100)
        }

        // Render initial content
        val currentScreen = navigationManager.getCurrentScreen()
        if (currentScreen is Screen.TransactionDetail) {
            renderTransactionDetail(currentScreen, contentElement!!)
        } else if (currentScreen is Screen.SavingsGoals) {
            renderTabContent(AppTabType.GOALS)
        } else if (currentScreen is Screen.Recommendations) {
            renderRecommendations(currentScreen, contentElement!!)
        } else if (currentScreen is Screen.Wiz3RCardDetail) {
            renderWiz3RCardDetail(currentScreen, contentElement!!)
        } else {
            renderTabContent(navigationManager.getCurrentTab())
        }

        return mainContainer
    }

    private fun updateTabButtonStyles(
        tabContainer: HTMLElement,
        activeTab: AppTabType,
    ) {
        val buttons = tabContainer.children
        for (i in 0 until buttons.length) {
            val button = buttons.item(i) as HTMLElement
            val isActive = i == activeTab.ordinal
            button.style.cssText = """
                flex: 1;
                padding: 1rem;
                border: none;
                background: ${if (isActive) "#2196F3" else "transparent"};
                color: ${if (isActive) "white" else "#333"};
                cursor: pointer;
                font-size: 0.9rem;
                transition: background-color 0.2s;
                border-bottom: 3px solid ${if (isActive) "#1976D2" else "transparent"};
                font-weight: ${if (isActive) "bold" else "normal"};
            """
        }
    }

    private fun renderTabContent(tab: AppTabType) {
        val rootContent = contentElement ?: return
        @Suppress("NAME_SHADOWING")
        var content = rootContent

        // When offline, show/hide tab containers instead of re-rendering
        if (NetworkMonitor.isOffline) {
            val targetContainer = tabContainers[tab]
            if (targetContainer != null) {
                // Hide all, show target
                tabContainers.values.forEach { it.style.display = "none" }
                targetContainer.style.display = "block"
                activeTabContainer = tab
                return
            }
            // Tab was never visited while online — show a message
            val container = (kotlinx.browser.document.createElement("div") as HTMLElement)
            val msg = kotlinx.browser.document.createElement("div") as HTMLElement
            msg.style.cssText = "color: #888; text-align: center; padding: 60px 20px; font-size: 15px;"
            msg.textContent = i18n.StringKey.OFFLINE_EDIT_BLOCKED.tr()
            container.appendChild(msg)
            rootContent.appendChild(container)
            tabContainers[tab] = container
            tabContainers.values.forEach { it.style.display = "none" }
            container.style.display = "block"
            activeTabContainer = tab
            return
        }

        // Reset scroll position when switching tabs — but not when refreshing
        // the same tab (e.g. after split/unsplit) where we want to restore position
        if (pendingScrollRestore == null) {
            window.scrollTo(0.0, 0.0)
        }

        // Online: wrap each tab's content in a persistent container div.
        // Remove old container for this tab (will be re-rendered fresh).
        tabContainers[tab]?.let { it.parentNode?.removeChild(it) }
        // Drop any non-tab-container children left behind by overlay screens
        // like Wiz3RCardDetail, which render directly into rootContent.
        // Without this, switching tabs from such a screen leaves the overlay
        // markup as a sibling above the new tab's content.
        val tracked = tabContainers.values.toSet()
        val toRemove = mutableListOf<HTMLElement>()
        for (i in 0 until rootContent.children.length) {
            val child = rootContent.children.item(i) as? HTMLElement ?: continue
            if (child !in tracked) toRemove += child
        }
        toRemove.forEach { it.parentNode?.removeChild(it) }
        // Hide all existing containers
        tabContainers.values.forEach { it.style.display = "none" }
        // Create a fresh container for this tab
        val container = (kotlinx.browser.document.createElement("div") as HTMLElement)
        rootContent.appendChild(container)
        tabContainers[tab] = container
        activeTabContainer = tab
        // Redirect content to the container so all rendering goes into it
        content = container

        // Check if a transaction refresh is needed (e.g., after split/unsplit)
        val needsRefresh = navigationManager.consumeTransactionRefresh()

        when (tab) {
            AppTabType.TRANSACTIONS -> {
                console.log("Rendering TRANSACTIONS tab. selectedAccountId: $selectedAccountId, needsRefresh: $needsRefresh")

                // Show loading state immediately
                content.innerHTML = """
                    <div style="display:flex;align-items:center;justify-content:center;min-height:60vh;color:#666;">
                        <div style="text-align:center;">
                            <div style="width:32px;height:32px;border:3px solid #333;border-top-color:#00d9ff;border-radius:50%;animation:spin 1s linear infinite;margin:0 auto 16px;"></div>
                            <style>@keyframes spin{to{transform:rotate(360deg)}}</style>
                        </div>
                    </div>
                """.trimIndent()

                // Check Plaid status first, then render either demo card or transaction list
                GlobalScope.launch {
                    var noAccountLinked = false
                    // Consume the one-shot Wiz 3R category-filter intent up front so the
                    // value survives whichever code path runs below and isn't re-read
                    // (and re-applied) by later renders on Clear or account click.
                    val pendingFilter = navigationManager.consumeCurrentScreenCategoryFilter()
                    // Handed to the component below so its loadTransactions() reuses this
                    // fetch instead of hitting /transactions a second time for the same data.
                    var preloadedTxnResponse: TransactionsResponse? = null
                    try {
                        val response = apiService.getTransactionsFromServer(forceRefresh = needsRefresh)
                        preloadedTxnResponse = response
                        accounts = response.accounts
                        // Re-validate the selection on every load, not only when it's
                        // null. A selected account id can outlive the accounts it
                        // referred to: the demo re-seed mints new account ids nightly, an
                        // account can be unlinked, and a session switch (real user ->
                        // demo, or a 401 -> re-auth) can carry a previous selection over.
                        // A stale id matches no account, so the list filters to zero rows
                        // and renders empty with nothing highlighted — and because the old
                        // guard only ran when null, it never recovered. Falling back to
                        // the first account makes that self-correcting.
                        val selectionIsStale =
                            selectedAccountId != null && accounts.none { it.account_id == selectedAccountId }
                        if ((selectedAccountId == null || selectionIsStale) && accounts.isNotEmpty()) {
                            // Restore selection saved by startRelinkFlow before its
                            // window.location.reload(), so a relink doesn't silently
                            // switch the user to a different account.
                            val saved = try {
                                kotlinx.browser.window.sessionStorage.getItem("wiz3r:selectedAccountId")
                            } catch (_: Throwable) { null }
                            selectedAccountId = if (saved != null && accounts.any { it.account_id == saved }) {
                                saved
                            } else {
                                accounts.first().account_id
                            }
                            if (saved != null) {
                                try { kotlinx.browser.window.sessionStorage.removeItem("wiz3r:selectedAccountId") } catch (_: Throwable) { }
                            }
                        }
                        // Wiz 3R "See this category" landed here with a category filter
                        // (already consumed above). If the currently selected account
                        // has no txns in that category, pre-switch to the account
                        // holding the most matching txns.
                        if (!pendingFilter.isNullOrBlank() && accounts.isNotEmpty()) {
                            val matchingByAccount = response.transactions
                                .filter { it.personal_finance_category?.primary.equals(pendingFilter, ignoreCase = true) }
                                .groupingBy { it.account_id }
                                .eachCount()
                            val currentMatches = matchingByAccount[selectedAccountId] ?: 0
                            if (currentMatches == 0) {
                                val bestAccount = matchingByAccount.maxByOrNull { it.value }?.key
                                if (bestAccount != null) selectedAccountId = bestAccount
                            }
                        }
                    } catch (e: Exception) {
                        console.error("Failed to load accounts: ${e.message}")
                        if (e.message == "AUTH_REQUIRED") {
                            navigationManager.logout()
                            return@launch
                        }
                        if (e.message?.contains("No Plaid access token", ignoreCase = true) == true) {
                            noAccountLinked = true
                        }
                    }

                    // Refresh subscription tier before rendering (prevents stale Pro gate)
                    try {
                        val status = apiService.getSubscriptionStatus()
                        subscriptionTier = status.tier
                        trialEndsAt = status.trialEndsAt
                    } catch (_: Exception) {
                        // Keep existing tier value on failure
                    }

                    // NOTE: the spinner is deliberately NOT cleared here. The heaviest
                    // work (loadTransactions' fetches + the row-mapping loop + building
                    // the list DOM) still happens below, so clearing it now leaves a
                    // blank panel for the whole duration — the app looks stuck. The
                    // spinner is torn down immediately before the real content is
                    // appended instead, so its lifetime matches the actual work.

                    // Wiz 3R one-shot intent (pendingFilter, consumed above) wins on
                    // first land. On user-driven re-renders — account click — the
                    // sticky filter carries forward whatever the component had
                    // selected, so a Wiz 3R filter (or one the user picked manually)
                    // survives until they hit Clear.
                    if (pendingFilter != null) stickyTransactionCategoryFilter = pendingFilter
                    val screenCategoryFilter = stickyTransactionCategoryFilter

                    var componentRef: SimpleTransactionListComponent? = null
                    val component = SimpleTransactionListComponent(
                        accessToken = navigationManager.getAccessToken(),
                        onTransactionClick = { transaction ->
                            componentRef?.let { cachedTransactions = it.getTransactions() }
                            showTransactionDetailModal(transaction)
                        },
                        onAuthError = {
                            navigationManager.logout()
                        },
                        onBackClick = {
                            selectedAccountId = null
                            renderTabContent(AppTabType.TRANSACTIONS)
                        },
                        accountId = selectedAccountId,
                        onNavClick = { label ->
                            when (label) {
                                "Activity" -> navigationManager.switchToTab(AppTabType.TRANSACTIONS)
                                "Dashboard" -> navigationManager.switchToTab(AppTabType.DASHBOARD)
                                "Goals" -> navigationManager.switchToTab(AppTabType.GOALS)
                                "Budget" -> navigationManager.switchToTab(AppTabType.BUDGET_PLANNING)
                                // The list's menu emits this too; without a branch
                                // it fell through the `when` and logging out from
                                // Activity silently did nothing at all.
                                "logout" -> menuAppNavigate("logout")
                            }
                        },
                        onSettingsClick = {
                            navigationManager.switchToTab(AppTabType.SETTINGS)
                        },
                        initialAccounts = accounts,
                        onAccountSwitch = { accountId ->
                            // Capture the live filter so a later FULL re-render (e.g. Back,
                            // or Clear) still carries it forward. The switch itself no longer
                            // goes through renderTabContent — switchAccount() re-filters the
                            // already-loaded data in place and re-renders with zero network
                            // calls, so the sticky category filter on the component is left
                            // untouched by the switch (it was never affected by it anyway).
                            stickyTransactionCategoryFilter = componentRef?.getCategoryFilter()
                            selectedAccountId = accountId
                            componentRef?.switchAccount(accountId)
                        },
                        onAddAccount = {
                            navigationManager.navigateTo(Screen.AccountLinking(daysRequested = 90))
                        },
                        onLogoClick = {
                            navigationManager.navigateTo(Screen.Landing)
                        },
                        forceRefresh = needsRefresh,
                        showDemoCard = noAccountLinked,
                        initialCategoryFilter = screenCategoryFilter,
                        onCategoryFilterCleared = {
                            stickyTransactionCategoryFilter = null
                            renderTabContent(AppTabType.TRANSACTIONS)
                        },
                        isProTier = { isProTier },
                        showProTag = { showProTag },
                        onSubscribeClick = {
                            navigationManager.navigateTo(Screen.Subscribe())
                        },
                        onProUpgradeClick = {
                            navigationManager.navigateTo(Screen.Subscribe(proOnly = true))
                        },
                        customCategories = customCategories,
                        onImportStatementClick = { acctId ->
                            navigationManager.navigateTo(Screen.StatementImport(appendToAccountId = acctId))
                        },
                        preloadedResponse = preloadedTxnResponse,
                    )
                    if (!noAccountLinked) {
                        component.loadTransactions(forceRefresh = needsRefresh)
                    }
                    componentRef = component
                    // Build the list DOM first, THEN swap the spinner out for it in one
                    // step, so there's never a blank panel between the two.
                    val listContent = component.render()
                    content.innerHTML = ""
                    content.appendChild(listContent)

                    // Email-link deep link: if the screen carries openTransactionId (from
                    // /?tx=<id>), find the matching txn now that data has loaded and open
                    // its detail modal. The URL was cleared at app start so a refresh
                    // won't reopen the modal.
                    val pendingOpenTxId = (navigationManager.getCurrentScreen() as? Screen.TransactionList)?.openTransactionId
                    if (pendingOpenTxId != null) {
                        component.getTransactions().firstOrNull { it.id == pendingOpenTxId }?.let { tx ->
                            showTransactionDetailModal(tx)
                        }
                    }

                    // Populate savings recommendation with goals data
                    if (!noAccountLinked) {
                        try {
                            val goalsVm = WebSavingsGoalsViewModel()
                            val userId = ApiService.Companion.getSharedUserId() ?: ""
                            goalsVm.onStateChanged = {
                                val goals = goalsVm.goals.filter { it.isActive }
                                // Compute available from component's loaded transactions
                                val now = kotlin.js.Date()
                                // The card is scoped to the last COMPLETE month, like the
                                // rest of the dashboard — on the 3rd of the month the
                                // current one is two days of data wearing a month's clothes.
                                val (dispYear, dispMonth) = utils.DashboardMonth.lastComplete(
                                    now.getFullYear(), now.getMonth() + 1,
                                )
                                val monthTx = component.getTransactions().filter { tx ->
                                    tx.date.getMonth() + 1 == dispMonth && tx.date.getFullYear() == dispYear
                                }
                                val income = monthTx.filter { !it.isDebit && !it.excludeFromTotals }.sumOf { it.amount }
                                val expenses = monthTx.filter { it.isDebit && !it.excludeFromTotals }.sumOf { -it.amount }
                                val available = income - expenses
                                // hasMonthData = anything landed; hasIncomeYet = pay arrived.
                                // The pair distinguishes "expenses-before-income" (autopay
                                // hit but payday hasn't) from "month-just-started" (nothing
                                // at all) and from genuine overspending.
                                val hasMonthData = income > 0 || expenses > 0
                                val hasIncomeYet = income > 0
                                component.setRecommendationHtml(
                                    buildSavingsRecommendationHtml(
                                        available, goals, utils.MonthNames.inSentence(dispMonth),
                                        hasMonthData, hasIncomeYet,
                                    )
                                )
                                // Attach click handlers to goal links + the empty-state CTA
                                kotlinx.browser.window.setTimeout({
                                    val goalLinks = kotlinx.browser.document.querySelectorAll(".goal-link")
                                    for (i in 0 until goalLinks.length) {
                                        val el = goalLinks.item(i) as? HTMLElement ?: continue
                                        el.addEventListener("click", { _ ->
                                            val goalId = el.getAttribute("data-goal-id") ?: return@addEventListener
                                            navigationManager.switchToTab(AppTabType.GOALS)
                                            kotlinx.browser.window.setTimeout({
                                                kotlinx.browser.document.getElementById("goal-$goalId")?.asDynamic()?.scrollIntoView(
                                                    js("{ behavior: 'smooth', block: 'center' }")
                                                )
                                            }, 500)
                                        })
                                    }
                                    // Empty-state CTA → open the Goals tab with the create dialog up
                                    kotlinx.browser.document.getElementById("recommendationCreateGoalBtn")?.addEventListener("click", { _ ->
                                        navigationManager.switchToTab(AppTabType.GOALS)
                                        navigationManager.navigateTo(Screen.SavingsGoals(openCreateDialog = true))
                                    })
                                    // "Review my budget" CTA when goals exist but $0 to save
                                    kotlinx.browser.document.getElementById("recommendationViewBudgetBtn")?.addEventListener("click", { _ ->
                                        navigationManager.switchToTab(AppTabType.BUDGET_PLANNING)
                                    })
                                }, 100)
                            }
                            goalsVm.refresh(userId)
                        } catch (e: Exception) {
                            console.log("Could not load savings recommendation: ${e.message}")
                        }
                    }

                    pendingScrollRestore?.let { y ->
                        pendingScrollRestore = null
                        kotlinx.browser.window.scrollTo(kotlinx.browser.window.scrollX, y)
                    }
                }
            }
            AppTabType.DASHBOARD -> {
                content.appendChild(WebDashboardRedesigned(
                    navigationManager = navigationManager,
                    accountId = selectedAccountId,
                    onAuthError = { navigationManager.logout() },
                    isDemoMode = isDemoMode,
                    useGridLayout = dashboardMode == "grid" && isProTier
                ).render())
            }
            AppTabType.PROJECTIONS -> {
                content.appendChild(WebChartsComponent(navigationManager, selectedAccountId).render())
            }
            AppTabType.GOALS -> {
                val component = WebSavingsGoalsComponent(navigationManager, false, isProTier, showProTag)
                content.appendChild(component.render())
            }
            AppTabType.BUDGET_PLANNING -> {
                content.appendChild(WebBudgetPlanningComponent(
                    navigationManager = navigationManager,
                    onAuthError = { navigationManager.logout() }
                ).render())
            }
            AppTabType.SETTINGS -> {
                content.appendChild(createSettingsContent(apiService))
            }
        }
    }

    private fun calculateTrialDaysLeft(): Int {
        val endStr = trialEndsAt ?: return 0
        return try {
            val withT = if (!endStr.contains("T")) endStr.replace(" ", "T") else endStr
            val normalized = if (!withT.endsWith("Z")) "${withT}Z" else withT
            val endDate = kotlin.js.Date(normalized)
            val now = kotlin.js.Date()
            val diffMs = endDate.getTime() - now.getTime()
            if (diffMs <= 0) 0
            else kotlin.math.ceil(diffMs / (1000.0 * 60 * 60 * 24)).toInt()
        } catch (_: Exception) { 0 }
    }

    private fun createSettingsContent(service: ApiService): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        val t = WebTheme.current
        val currentSavingsTarget = settingsManager.getSavingsTargetPercentage().toInt()
        val isMobile = ResponsiveUtils.isMobile()

        container.innerHTML = """
            <div style="background: transparent; min-height: 100dvh;">
                <!-- Mobile Menu Overlay (hidden by default) -->
                ${if (isMobile) MarketingMenu.mobileOverlayHtml() else ""}

                <!-- Top nav bar (responsive) -->
                ${if (isMobile) """
                <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: sticky; top: 0; z-index: 100;">
                    <img id="settings-logo" src="${if (t.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"}" alt="wiz≡r" style="height: 32px; cursor: pointer;" title="Go to Home">
                    <div style="display: flex; align-items: center; gap: 8px;">
                        <span id="nav-language" style="padding: 4px 8px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border};" title="Click to change language">${languageToggleLabel()}</span>
                        <button id="hamburgerMenu" style="background: none; border: none; color: ${t.textPrimary}; font-size: 24px; cursor: pointer; padding: 4px;">☰</button>
                    </div>
                </div>
                """ else """
                <div id="settings-navbar" style="display: flex; align-items: center; padding: 16px 32px; background: ${t.navBackground}; border-bottom: 1px solid ${t.border}; position: fixed; top: 0; left: 0; right: 0; z-index: 100; transition: padding 0.25s ease;">
                    <img id="settings-logo" src="${if (t.isDark) "/wiz3r_logo.png?v=3" else "/wiz3r_on_white.png?v=2"}" alt="wiz≡r" style="height: 56px; cursor: pointer; margin-right: 40px; transition: height 0.25s ease;" title="Go to Home">
                    <div style="display: flex; gap: 8px; flex: 1;">
                        <span id="nav-activity" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_TRANSACTIONS.tr()}</span>
                        <span id="nav-dashboard" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_DASHBOARD.tr()}</span>
                        <span id="nav-goals" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_GOALS.tr()}</span>
                        <span id="nav-budget" style="padding: 8px 16px; border-radius: 6px; font-size: 13px; font-weight: 500; cursor: pointer; background: transparent; color: ${t.navText};">${StringKey.NAV_BUDGET.tr()}</span>
                    </div>
                    <div style="display: flex; align-items: center; gap: 12px;">
                        <span id="nav-language" style="padding: 4px 10px; border-radius: 4px; background: ${t.navBackground}; color: ${t.accent}; font-size: 12px; font-weight: 600; cursor: pointer; border: 1px solid ${t.border}; transition: all 0.2s;" title="Click to change language">${languageToggleLabel()}</span>
                        <span id="nav-settings" style="width: 32px; height: 32px; border-radius: 50%; background: ${t.navBackground}; display: flex; align-items: center; justify-content: center; font-size: 16px; cursor: pointer; border: 1px solid ${t.border};">&#9776;</span>
                    </div>
                </div>
                <div style="height: 80px;"></div>
                """}

                <!-- Main content -->
                <div style="max-width: 1200px; width: 100%; margin: 0 auto; padding: ${if (isMobile) "16px" else "32px 48px"}; box-sizing: border-box;">
                    <h2 style="color: ${t.textPrimary}; margin-bottom: 4px; font-size: ${if (isMobile) "28px" else "32px"}; font-weight: 600;">${Strings[StringKey.SETTINGS_TITLE]}</h2>
                    <p style="color: ${t.textSecondary}; font-size: 14px; margin: 0 0 2rem 0;">${if (LocaleManager.getCurrentLocale().code == "fr") "G\u00e9rez votre compte, vos pr\u00e9f\u00e9rences et vos connexions bancaires" else "Manage your account, preferences, and bank connections"}</p>

                    <!-- Row 1: Savings Target + Language (3fr 2fr) -->
                    <div style="display: grid; grid-template-columns: ${if (isMobile) "1fr" else "3fr 2fr"}; gap: 20px; margin-bottom: 20px;">

                    <!-- Savings Target Section -->
                    <div style="background: ${t.card}; border-radius: 12px; padding: 1.5rem; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};">
                        <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
                            <div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: #22C55E; border-radius: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 12a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M12 7a5 5 0 1 0 5 5"/><path d="M13 3.055a9 9 0 1 0 7.941 7.945"/><path d="M15 6v3h3l3 -3h-3v-3l-3 3"/><path d="M15 9l-3 3"/></svg></div>
                            <div>
                                <h3 style="margin: 0; color: ${t.textPrimary}; font-size: 16px; font-weight: 500;">${StringKey.SETTINGS_SAVINGS_TARGET.tr()}</h3>
                                <p style="margin: 4px 0 0 0; color: #888; font-size: 13px;">${StringKey.SETTINGS_SAVINGS_TARGET_HELP.tr()}</p>
                            </div>
                        </div>
                        <div style="display: flex; align-items: center; gap: 16px;">
                            <input type="range" id="savings-target-slider" min="5" max="50" step="5" value="$currentSavingsTarget"
                                   style="flex: 1; height: 8px; -webkit-appearance: none; background: ${t.border}; border-radius: 4px; cursor: pointer; outline: none;">
                            <span id="savings-target-value" style="color: ${t.accent}; font-size: 24px; font-weight: 700; min-width: 60px; text-align: right;">
                                $currentSavingsTarget%
                            </span>
                        </div>
                        <p style="color: #666; font-size: 12px; margin-top: 12px; margin-bottom: 0;"></p>
                    </div>

                    <!-- Dashboard Mode + Theme + Language Section -->
                    <div style="background: ${t.card}; border-radius: 12px; padding: 1.5rem; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};">
                        <!-- Theme Preference -->
                        <h3 style="color: ${t.textSecondary}; margin-bottom: 0.75rem; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500;">${StringKey.SETTINGS_APPEARANCE.tr()}</h3>
                        <div style="display: flex; gap: 8px; margin-bottom: 1.25rem;">
                            <button id="theme-system-btn" style="
                                flex: 1; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
                                border: 1px solid ${if ((kotlinx.browser.localStorage.getItem("theme_preference") ?: "system") == "system") "${t.accent}66" else "${t.border}"};
                                background: ${if ((kotlinx.browser.localStorage.getItem("theme_preference") ?: "system") == "system") "${t.accent}1A" else "transparent"};
                                color: ${if ((kotlinx.browser.localStorage.getItem("theme_preference") ?: "system") == "system") "${t.accent}" else "${t.textSecondary}"};
                                transition: all 0.15s;
                            ">${StringKey.SETTINGS_THEME_SYSTEM.tr()}</button>
                            <button id="theme-dark-btn" style="
                                flex: 1; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
                                border: 1px solid ${if (kotlinx.browser.localStorage.getItem("theme_preference") == "dark") "${t.accent}66" else "${t.border}"};
                                background: ${if (kotlinx.browser.localStorage.getItem("theme_preference") == "dark") "${t.accent}1A" else "transparent"};
                                color: ${if (kotlinx.browser.localStorage.getItem("theme_preference") == "dark") "${t.accent}" else "${t.textSecondary}"};
                                transition: all 0.15s;
                            ">${StringKey.SETTINGS_THEME_DARK.tr()}</button>
                            <button id="theme-light-btn" style="
                                flex: 1; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
                                border: 1px solid ${if (kotlinx.browser.localStorage.getItem("theme_preference") == "light") "${t.accent}66" else "${t.border}"};
                                background: ${if (kotlinx.browser.localStorage.getItem("theme_preference") == "light") "${t.accent}1A" else "transparent"};
                                color: ${if (kotlinx.browser.localStorage.getItem("theme_preference") == "light") "${t.accent}" else "${t.textSecondary}"};
                                transition: all 0.15s;
                            ">${StringKey.SETTINGS_THEME_LIGHT.tr()}</button>
                        </div>

                        <!-- Dashboard Mode -->
                        <h3 style="color: ${t.textSecondary}; margin-bottom: 0.75rem; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500;">Dashboard</h3>
                        <div style="display: flex; gap: 8px; margin-bottom: 1.25rem;">
                            <button id="dashboard-mode-standard" style="
                                flex: 1; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
                                border: 1px solid ${if (dashboardMode == "standard") "${t.accent}66" else "${t.border}"};
                                background: ${if (dashboardMode == "standard") "${t.accent}1A" else "transparent"};
                                color: ${if (dashboardMode == "standard") "${t.accent}" else "${t.textSecondary}"};
                                transition: all 0.15s;
                            ">${if (LocaleManager.getCurrentLocale().code == "fr") "Standard" else "Standard"}</button>
                            <button id="dashboard-mode-grid" style="
                                flex: 1; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
                                border: 1px solid ${if (dashboardMode == "grid" && isProTier) "${t.accent}66" else "${t.border}"};
                                background: ${if (dashboardMode == "grid" && isProTier) "${t.accent}1A" else "transparent"};
                                color: ${if (dashboardMode == "grid" && isProTier) "${t.accent}" else "${t.textSecondary}"};
                                transition: all 0.15s;
                            ">
                                <span style="display: flex; align-items: center; justify-content: center; gap: 6px;">
                                    ${if (LocaleManager.getCurrentLocale().code == "fr") "Grille personnalisable" else "Customizable Grid"}
                                    ${if (showProTag) """<span style="background: linear-gradient(135deg, #FFD700, #FFA500); color: #000; font-size: 9px; font-weight: 700; padding: 1px 5px; border-radius: 3px; letter-spacing: 0.3px;">PRO</span>""" else ""}
                                </span>
                            </button>
                        </div>

                        <!-- Language -->
                        <h3 style="color: ${t.textSecondary}; margin-bottom: 0.75rem; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500;">${StringKey.SETTINGS_LANGUAGE.tr()}</h3>
                        <div style="display: flex; gap: 8px;">
                            <button id="lang-en-btn" style="
                                flex: 1; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;
                                border: 1px solid ${if (LocaleManager.getCurrentLocale().code == "en") "${t.accent}66" else "${t.border}"};
                                background: ${if (LocaleManager.getCurrentLocale().code == "en") "${t.accent}1A" else "transparent"};
                                color: ${if (LocaleManager.getCurrentLocale().code == "en") "${t.accent}" else "${t.textSecondary}"};
                                transition: all 0.15s;
                            ">EN &nbsp;English</button>
                            <button id="lang-fr-btn" style="
                                flex: 1; padding: 10px 12px; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;
                                border: 1px solid ${if (LocaleManager.getCurrentLocale().code == "fr") "${t.accent}66" else "${t.border}"};
                                background: ${if (LocaleManager.getCurrentLocale().code == "fr") "${t.accent}1A" else "transparent"};
                                color: ${if (LocaleManager.getCurrentLocale().code == "fr") "${t.accent}" else "${t.textSecondary}"};
                                transition: all 0.15s;
                            ">FR &nbsp;Français</button>
                        </div>

                    </div>

                    </div><!-- end row 1 -->

                    <!-- Row 2: Notifications + Banks (2fr 3fr) -->
                    <div style="display: grid; grid-template-columns: ${if (isMobile) "1fr" else "2fr 3fr"}; gap: 20px; margin-bottom: 20px;">

                    <!-- Transaction Notifications Section -->
                    <div id="notif-settings-section" style="background: ${t.card}; border-radius: 12px; padding: 1.5rem; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow}; ${if (!isProTier) "cursor: pointer;" else ""}">
                        <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
                            <div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: #F59E0B; border-radius: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6"/><path d="M9 17v1a3 3 0 0 0 6 0v-1"/></svg></div>
                            <div style="flex: 1;">
                                <h3 style="margin: 0; color: ${t.textPrimary}; font-size: 16px; font-weight: 500; display: flex; align-items: center; gap: 8px;">${StringKey.NOTIF_SETTINGS_TITLE.tr()}${if (showProTag) """<span style="background: ${t.proBadge}; color: ${t.proBadgeText}; font-size: 9px; font-weight: 700; padding: 2px 6px; border-radius: 4px; letter-spacing: 0.5px;">PRO</span>""" else ""}</h3>
                                ${if (!isProTier) """<p style="margin: 4px 0 0 0; color: #F59E0B; font-size: 13px; font-weight: 500;">${StringKey.NOTIF_SETTINGS_UPGRADE_HINT.tr()}</p>""" else """<p style="margin: 4px 0 0 0; color: #888; font-size: 13px;">${StringKey.NOTIF_SETTINGS_ENABLE.tr()}</p>"""}
                            </div>
                            ${if (isProTier) """
                            <label style="position: relative; display: inline-block; width: 44px; height: 24px; cursor: pointer;">
                                <input type="checkbox" id="notif-toggle" style="opacity: 0; width: 0; height: 0;">
                                <span id="notif-toggle-track" style="position: absolute; inset: 0; background: ${t.border}; border-radius: 12px; transition: background 0.2s;"></span>
                                <span id="notif-toggle-thumb" style="position: absolute; top: 2px; left: 2px; width: 20px; height: 20px; background: #fff; border-radius: 50%; transition: transform 0.2s;"></span>
                            </label>
                            """ else ""}
                        </div>
                        ${if (isProTier) """
                        <div id="notif-options" style="display: none;">
                            <!-- Channel selector -->
                            <p style="color: ${t.textSecondary}; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500; margin: 1rem 0 0.5rem 0;">${StringKey.NOTIF_SETTINGS_CHANNEL.tr()}</p>
                            <div id="channel-email-btn" style="display: flex; align-items: center; padding: 14px 0; cursor: pointer; border-bottom: 1px solid ${t.border};">
                                <span style="font-size: 15px; color: ${t.textPrimary};">${StringKey.NOTIF_SETTINGS_EMAIL.tr()}</span>
                                <span id="channel-email-check" style="margin-left: auto; color: ${t.accent}; font-size: 18px;"></span>
                            </div>
                            <div id="channel-sms-btn" style="display: flex; align-items: center; padding: 14px 0; cursor: pointer; border-bottom: 1px solid ${t.border};">
                                <span style="font-size: 15px; color: ${t.textPrimary};">${StringKey.NOTIF_SETTINGS_SMS.tr()}</span>
                                <span id="channel-sms-check" style="margin-left: auto; color: ${t.accent}; font-size: 18px;"></span>
                            </div>
                            <div id="channel-both-btn" style="display: flex; align-items: center; padding: 14px 0; cursor: pointer;">
                                <span style="font-size: 15px; color: ${t.textPrimary};">${StringKey.NOTIF_SETTINGS_BOTH.tr()}</span>
                                <span id="channel-both-check" style="margin-left: auto; color: ${t.accent}; font-size: 18px;"></span>
                            </div>

                            <!-- Phone input (shown when SMS or Both selected) -->
                            <div id="notif-phone-section" style="display: none; margin-top: 1rem;">
                                <p style="color: ${t.textSecondary}; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500; margin-bottom: 0.5rem;">${StringKey.NOTIF_SETTINGS_PHONE.tr()}</p>
                                <div style="display: flex; gap: 8px; align-items: center;">
                                    <input type="tel" id="notif-phone-input" placeholder="+1 (555) 123-4567" style="
                                        flex: 1; padding: 12px; border: 1px solid ${t.border}; border-radius: 8px;
                                        background: ${t.background}; color: ${t.textPrimary}; font-size: 14px; box-sizing: border-box;
                                    " />
                                    <!-- Verify button OR inline "✓ Verified" badge — they
                                         share the slot to the right of the phone input. Only
                                         one is visible at a time, no second line below. -->
                                    <button id="notif-verify-btn" style="
                                        padding: 12px 20px; border: none; background: ${if (t.isDark) "#00d9ff" else t.accentStrong}; color: ${if (t.isDark) "#000" else "#fff"};
                                        border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;
                                        white-space: nowrap; transition: background 0.2s;
                                    ">${StringKey.NOTIF_SETTINGS_VERIFY.tr()}</button>
                                    <span id="notif-phone-status" style="
                                        display: none; color: #22C55E; font-size: 14px; font-weight: 500;
                                        white-space: nowrap; padding: 12px 8px;
                                    "></span>
                                </div>

                                <!-- SMS Consent -->
                                <div id="notif-sms-consent" style="margin-top: 1rem;">
                                    <label style="display: flex; align-items: flex-start; gap: 8px; cursor: pointer;">
                                        <input type="checkbox" id="notif-consent-checkbox" style="margin-top: 3px; accent-color: ${t.accent};">
                                        <span style="color: ${t.textSecondary}; font-size: 12px; line-height: 1.4;">${StringKey.NOTIF_SETTINGS_SMS_CONSENT.tr()}</span>
                                    </label>
                                </div>
                            </div>

                            <!-- Send test notification (parity with iOS) -->
                            <div style="margin-top: 1rem;">
                                <button id="notif-test-fire-btn" style="
                                    padding: 10px 16px; border: 1px solid ${t.border}; background: transparent; color: ${t.accent};
                                    border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
                                    display: inline-flex; align-items: center; gap: 6px;
                                ">
                                    <span style="font-size: 14px;">✈</span>
                                    <span>${i18n.StringKey.NOTIF_TEST_SEND.tr()}</span>
                                </button>
                                <div id="notif-test-fire-result" style="display: none; font-size: 12px; margin-top: 6px;"></div>
                            </div>
                        </div>
                        """ else ""}
                    </div>

                    <!-- Linked Banks + Statement Accounts (stacked in one cell) -->
                    <div style="display: flex; flex-direction: column; gap: 20px;">
                        <!-- Linked Banks Section -->
                        <div id="linked-banks-section" style="background: ${t.card}; border-radius: 12px; padding: 1.5rem; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow};">
                            <h3 style="color: ${t.textSecondary}; margin-bottom: 1rem; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500;">${StringKey.SETTINGS_LINKED_BANKS.tr()}</h3>
                            <div id="linked-banks-list" style="color: ${t.textSecondary}; font-size: 14px;">${StringKey.LABEL_LOADING.tr()}</div>
                        </div>

                        <!-- Statement Accounts Section -->
                        <div id="statement-accounts-section" style="background: ${t.card}; border-radius: 12px; padding: 1.5rem; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow}; display: none;">
                            <h3 style="color: ${t.textSecondary}; margin-bottom: 1rem; font-size: 12px; text-transform: uppercase; letter-spacing: 0.5px; font-weight: 500;">${StringKey.SETTINGS_STATEMENT_ACCOUNTS.tr()}</h3>
                            <div id="statement-accounts-list" style="color: ${t.textSecondary}; font-size: 14px;"></div>
                        </div>
                    </div>

                    </div><!-- end row 2 -->

                    <!-- Row 3: MFA + Subscription (1fr 1fr) -->
                    <div style="display: grid; grid-template-columns: ${if (isMobile) "1fr" else "1fr 1fr"}; gap: 20px; margin-bottom: 20px;">

                    <!-- MFA Section (only shown when MFA is available in production mode) -->
                    <div id="mfa-settings-section" style="background: ${t.card}; border-radius: 12px; padding: 1.5rem; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow}; display: none;">
                        <div style="display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem;">
                            <div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: #3B82F6; border-radius: 8px;"><svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3a12 12 0 0 0 8.5 3a12 12 0 0 1 -8.5 15a12 12 0 0 1 -8.5 -15a12 12 0 0 0 8.5 -3"/><path d="M12 11m-1 0a1 1 0 1 0 2 0a1 1 0 1 0 -2 0"/><path d="M12 12l0 2.5"/></svg></div>
                            <div style="flex: 1;">
                                <h3 style="margin: 0; color: ${t.textPrimary}; font-size: 16px; font-weight: 500;">${StringKey.SETTINGS_MFA.tr()}</h3>
                                <p id="mfa-status-text" style="margin: 4px 0 0 0; color: ${t.textSecondary}; font-size: 13px;">${StringKey.LABEL_LOADING.tr()}</p>
                            </div>
                            <div id="mfa-status-badge" style="padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500;"></div>
                        </div>
                        <div id="mfa-actions" style="margin-top: 1rem;">
                            <!-- Enable MFA button (shown when MFA is disabled) -->
                            <button id="enable-mfa-btn" style="
                                display: none;
                                width: 100%;
                                padding: 12px;
                                border: none;
                                background: #3B82F6;
                                color: white;
                                border-radius: 8px;
                                cursor: pointer;
                                font-size: 14px;
                                font-weight: 500;
                                transition: background-color 0.2s;
                            " onmouseover="this.style.backgroundColor='#2563EB'" onmouseout="this.style.backgroundColor='#3B82F6'">
                                ${StringKey.SETTINGS_MFA_ENABLE.tr()}
                            </button>
                            <!-- Disable MFA button (shown when MFA is enabled) -->
                            <button id="disable-mfa-btn" style="
                                display: none;
                                width: 100%;
                                padding: 12px;
                                border: 1px solid #666;
                                background: transparent;
                                color: #ff4757;
                                border-radius: 8px;
                                cursor: pointer;
                                font-size: 14px;
                                font-weight: 500;
                                transition: all 0.2s;
                            " onmouseover="this.style.backgroundColor='#1a1a1a'" onmouseout="this.style.backgroundColor='transparent'">
                                ${StringKey.SETTINGS_MFA_DISABLE.tr()}
                            </button>
                        </div>
                    </div>

                    <!-- Subscription Status Section — clickable to expand -->
                    <div id="subscription-card" style="background: ${t.card}; border-radius: 12px; padding: 1.5rem; border: 1px solid ${t.border}; box-shadow: ${t.cardShadow}; cursor: pointer; transition: border-color 0.2s;" onmouseover="this.style.borderColor='#333'" onmouseout="this.style.borderColor='#1f1f1f'">
                        <div style="display: flex; align-items: center; gap: 1rem;">
                            <div style="display: flex; align-items: center; justify-content: center; width: 36px; height: 36px; background: ${if (isProTier) "linear-gradient(135deg, #B8860B, #DAA520)" else if (isTrialing) "#6366F1" else "#555"}; border-radius: 8px;">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    ${if (isProTier)
                                        """<path d="M6 5h12l3 5l-8.5 9.5a.7 .7 0 0 1 -1 0l-8.5 -9.5l3 -5"/><path d="M10 12l-2 -2.2l.6 -1"/>"""
                                    else if (isTrialing)
                                        """<path d="M8 21l8 0"/><path d="M12 17l0 4"/><path d="M7 4l10 0"/><path d="M17 4v8a5 5 0 0 1 -10 0v-8"/><path d="M3 9a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/><path d="M17 9a2 2 0 1 0 4 0a2 2 0 1 0 -4 0"/>"""
                                    else
                                        """<path d="M12 2L15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26Z"/>"""}
                                </svg>
                            </div>
                            <div style="flex: 1;">
                                <h3 style="margin: 0; color: ${t.textPrimary}; font-size: 16px; font-weight: 500;">
                                    ${if (isTrialing) {
                                        val days = calculateTrialDaysLeft()
                                        if (LocaleManager.getCurrentLocale().code == "fr") "Essai gratuit \u2014 $days jour${if (days != 1) "s" else ""}"
                                        else "Free Trial \u2014 $days day${if (days != 1) "s" else ""} left"
                                    } else if (isProTier) {
                                        if (LocaleManager.getCurrentLocale().code == "fr") "Abonnement Pro" else "Current Plan: Pro subscription"
                                    } else {
                                        if (LocaleManager.getCurrentLocale().code == "fr") "Abonnement Standard" else "Current Plan: Standard"
                                    }}
                                </h3>
                                <p style="margin: 4px 0 0 0; color: #888; font-size: 13px;">
                                    ${if (isTrialing) {
                                        if (LocaleManager.getCurrentLocale().code == "fr") "Toutes les fonctionnalit\u00e9s actives" else "All features active"
                                    } else if (isProTier) {
                                        if (LocaleManager.getCurrentLocale().code == "fr") "Actif" else "Active"
                                    } else {
                                        if (LocaleManager.getCurrentLocale().code == "fr") "Actif" else "Active"
                                    }}
                                </p>
                            </div>
                            <svg id="subscription-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" style="transition: transform 0.2s;">
                                <polyline points="6 9 12 15 18 9"/>
                            </svg>
                        </div>

                        <!-- Expandable details (hidden by default) -->
                        <div id="subscription-details" style="display: none; margin-top: 16px; padding-top: 16px; border-top: 1px solid #1f1f1f;">
                            ${if (isTrialing) """
                            <p style="color: #aaa; font-size: 13px; line-height: 1.5; margin: 0 0 12px;">
                                ${if (LocaleManager.getCurrentLocale().code == "fr") "Profitez de toutes les fonctionnalit\u00e9s pendant votre essai. Choisissez un plan avant la fin pour continuer."
                                else "Enjoy all features during your trial. Choose a plan before it ends to keep access."}
                            </p>
                            <button id="settings-subscribe-cta" style="
                                width: 100%; padding: 12px; border: none;
                                background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                                color: #0a0a0a; border-radius: 8px; cursor: pointer;
                                font-size: 14px; font-weight: 600; transition: opacity 0.2s;
                            " onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                                ${if (LocaleManager.getCurrentLocale().code == "fr") "Choisir un plan" else "Choose a Plan"}
                            </button>
                            """ else if (!isProTier) """
                            <p style="color: #aaa; font-size: 13px; line-height: 1.5; margin: 0 0 12px;">
                                ${if (LocaleManager.getCurrentLocale().code == "fr") "Passez \u00e0 Pro pour les divisions, r\u00e9partitions, alertes et plus."
                                else "Upgrade to Pro for split, distribute, alerts & more."}
                            </p>
                            <button id="settings-subscribe-cta" style="
                                width: 100%; padding: 12px; border: none;
                                background: linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%);
                                color: white; border-radius: 8px; cursor: pointer;
                                font-size: 14px; font-weight: 600; transition: opacity 0.2s;
                            " onmouseover="this.style.opacity='0.85'" onmouseout="this.style.opacity='1'">
                                ${if (LocaleManager.getCurrentLocale().code == "fr") "Passer \u00e0 Pro" else "Upgrade to Pro"}
                            </button>
                            """ else """
                            <p style="color: #aaa; font-size: 13px; line-height: 1.5; margin: 0;">
                                ${if (LocaleManager.getCurrentLocale().code == "fr") "Merci de soutenir wiz\u2261r. Vous avez acc\u00e8s \u00e0 toutes les fonctionnalit\u00e9s Pro."
                                else "Thank you for supporting wiz\u2261r. You have access to all Pro features."}
                            </p>
                            """}

                            <!-- Manage Subscription — opens Stripe Customer Portal for Stripe subscribers;
                                 for Apple/Google subscribers, expands an inline panel pointing them to
                                 the right native subscription manager. Required under guideline 3.1.2(b). -->
                            <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid #1a1a1a;">
                                <button id="manage-subscription-btn" style="
                                    width: 100%; padding: 10px; border: 1px solid #00d9ff;
                                    background: transparent; color: #00d9ff; border-radius: 8px;
                                    cursor: pointer; font-size: 13px; font-weight: 500;
                                    display: flex; align-items: center; justify-content: center;
                                    gap: 6px; transition: background 0.2s;
                                " onmouseover="this.style.background='rgba(0,217,255,0.08)'" onmouseout="this.style.background='transparent'">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <rect x="2" y="6" width="20" height="14" rx="2"/>
                                        <line x1="2" y1="11" x2="22" y2="11"/>
                                    </svg>
                                    ${StringKey.SETTINGS_MANAGE_SUBSCRIPTION.tr()}
                                </button>
                                <!-- Channel-picker panel — shown when the user has no Stripe customer
                                     (i.e., they subscribed via Apple, Google, or were admin-set). -->
                                <div id="manage-subscription-channels" style="display: none; margin-top: 12px;">
                                    <p style="color: #aaa; font-size: 12px; margin: 0 0 10px; line-height: 1.5;">
                                        ${if (LocaleManager.getCurrentLocale().code == "fr")
                                            "Votre abonnement est géré par la plateforme où vous l’avez souscrit. Choisissez ci-dessous :"
                                        else
                                            "Your subscription is managed by the platform you subscribed through. Pick one below:"}
                                    </p>
                                    <div style="display: flex; flex-direction: column; gap: 8px;">
                                        <a href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noopener" style="
                                            display: flex; align-items: center; gap: 10px; padding: 10px 12px;
                                            background: #1a1a1e; border: 1px solid #2a2a2e; border-radius: 8px;
                                            color: #fff; text-decoration: none; font-size: 13px;
                                            transition: border-color 0.15s, background 0.15s;
                                        " onmouseover="this.style.borderColor='#00d9ff'; this.style.background='#1f1f24'" onmouseout="this.style.borderColor='#2a2a2e'; this.style.background='#1a1a1e'">
                                            <span style="font-size: 18px;"></span>
                                            <span style="flex: 1;">
                                                <strong>${if (LocaleManager.getCurrentLocale().code == "fr") "Abonné via iOS" else "Subscribed on iOS"}</strong><br>
                                                <span style="color: #888; font-size: 11px;">${if (LocaleManager.getCurrentLocale().code == "fr") "Ouvrir vos abonnements Apple" else "Open your Apple Subscriptions"}</span>
                                            </span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17l9.2-9.2M17 17V7H7"/></svg>
                                        </a>
                                        <a href="https://play.google.com/store/account/subscriptions" target="_blank" rel="noopener" style="
                                            display: flex; align-items: center; gap: 10px; padding: 10px 12px;
                                            background: #1a1a1e; border: 1px solid #2a2a2e; border-radius: 8px;
                                            color: #fff; text-decoration: none; font-size: 13px;
                                            transition: border-color 0.15s, background 0.15s;
                                        " onmouseover="this.style.borderColor='#00d9ff'; this.style.background='#1f1f24'" onmouseout="this.style.borderColor='#2a2a2e'; this.style.background='#1a1a1e'">
                                            <span style="font-size: 18px;">▶</span>
                                            <span style="flex: 1;">
                                                <strong>${if (LocaleManager.getCurrentLocale().code == "fr") "Abonné via Android" else "Subscribed on Android"}</strong><br>
                                                <span style="color: #888; font-size: 11px;">${if (LocaleManager.getCurrentLocale().code == "fr") "Ouvrir vos abonnements Google Play" else "Open your Google Play subscriptions"}</span>
                                            </span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17l9.2-9.2M17 17V7H7"/></svg>
                                        </a>
                                        <a href="mailto:support@wiz3r.com" style="
                                            display: flex; align-items: center; gap: 10px; padding: 10px 12px;
                                            background: #1a1a1e; border: 1px solid #2a2a2e; border-radius: 8px;
                                            color: #fff; text-decoration: none; font-size: 13px;
                                            transition: border-color 0.15s, background 0.15s;
                                        " onmouseover="this.style.borderColor='#00d9ff'; this.style.background='#1f1f24'" onmouseout="this.style.borderColor='#2a2a2e'; this.style.background='#1a1a1e'">
                                            <span style="font-size: 18px;">✉</span>
                                            <span style="flex: 1;">
                                                <strong>${if (LocaleManager.getCurrentLocale().code == "fr") "Autre cas — Contacter le soutien" else "Other — Contact Support"}</strong><br>
                                                <span style="color: #888; font-size: 11px;">support@wiz3r.com</span>
                                            </span>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17l9.2-9.2M17 17V7H7"/></svg>
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <!-- Delete account buried here -->
                            <div style="margin-top: 20px; padding-top: 12px; border-top: 1px solid #1a1a1a; text-align: center;">
                                <a id="deleteAccountLink" href="#" style="color: #555; font-size: 11px; text-decoration: none; transition: color 0.2s;" onmouseover="this.style.color='#888'" onmouseout="this.style.color='#555'">
                                    ${StringKey.SETTINGS_DELETE_ACCOUNT.tr()}
                                </a>
                            </div>
                        </div>
                    </div>

                    </div><!-- end row 3 -->

                    <!-- Delete Account First Confirmation (soft step) -->
                    <div id="delete-account-soft-confirm" style="display: none; background: #141414; border-radius: 12px; padding: 1.5rem; margin-top: 0.5rem; border: 1px solid #2a2a2e;">
                        <p style="color: #ccc; font-size: 14px; margin: 0 0 1rem 0;">
                            ${if (LocaleManager.getCurrentLocale().code == "fr") "Vous \u00eates s\u00fbr ? On aimerait vous aider si quelque chose ne va pas." else "Are you sure? We'd love to help if something isn't right."}
                        </p>
                        <div style="display: flex; gap: 1rem; align-items: center;">
                            <button id="delete-account-contact-support" style="
                                padding: 10px 20px; border: 1px solid #00d9ff; background: transparent;
                                color: #00d9ff; border-radius: 8px; cursor: pointer; font-size: 13px; font-weight: 500;
                                transition: background 0.2s;
                            " onmouseover="this.style.background='rgba(0,217,255,0.08)'" onmouseout="this.style.background='transparent'">
                                ${StringKey.FOOTER_CONTACT.tr()}
                            </button>
                            <a id="delete-account-proceed" href="#" style="color: #888; font-size: 12px; text-decoration: none; transition: color 0.2s;" onmouseover="this.style.color='#ff6666'" onmouseout="this.style.color='#888'">
                                ${if (LocaleManager.getCurrentLocale().code == "fr") "Oui, je veux supprimer" else "Yes, I want to delete"}
                            </a>
                        </div>
                    </div>

                    <!-- MFA Setup Modal (hidden by default) -->
                    <div id="mfa-setup-modal" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 1000; align-items: center; justify-content: center;">
                        <div style="background: #1a1a1a; border-radius: 16px; padding: 2rem; max-width: 400px; width: 90%; max-height: 90vh; overflow-y: auto; border: 1px solid #333;">
                            <h3 style="color: #fff; margin: 0 0 1rem 0; font-size: 20px;">${StringKey.SETTINGS_MFA_SETUP.tr()}</h3>
                            <p style="color: #888; font-size: 14px; margin-bottom: 1.5rem;">${StringKey.MFA_SCAN_QR.tr()}</p>
                            <div id="mfa-qr-container" style="background: #fff; padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem; text-align: center;">
                                <img id="mfa-qr-code" style="max-width: 200px;" />
                            </div>
                            <p style="color: #888; font-size: 12px; margin-bottom: 0.5rem;">${StringKey.MFA_MANUAL_CODE.tr()}</p>
                            <code id="mfa-secret-code" style="display: block; background: #0a0a0a; padding: 12px; border-radius: 6px; color: #00d9ff; font-size: 14px; margin-bottom: 1.5rem; word-break: break-all;"></code>
                            <div style="margin-bottom: 1rem;">
                                <label style="color: #fff; font-size: 14px; display: block; margin-bottom: 0.5rem;">${StringKey.MFA_ENTER_VERIFICATION_CODE.tr()}</label>
                                <input id="mfa-setup-code-input" type="text" placeholder="000000" maxlength="6" style="
                                    width: 100%;
                                    padding: 12px;
                                    border: 1px solid #333;
                                    border-radius: 8px;
                                    background: #0a0a0a;
                                    color: #fff;
                                    font-size: 18px;
                                    text-align: center;
                                    letter-spacing: 8px;
                                    box-sizing: border-box;
                                " />
                            </div>
                            <p id="mfa-setup-error" style="color: #ff4757; font-size: 13px; margin-bottom: 1rem; display: none;"></p>
                            <div style="background: #0a0a0a; padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
                                <p style="color: #fff; font-size: 14px; font-weight: 500; margin: 0 0 0.5rem 0;">${StringKey.MFA_BACKUP_CODES_TITLE.tr()}</p>
                                <p style="color: #888; font-size: 12px; margin: 0 0 0.5rem 0;">${StringKey.MFA_BACKUP_CODES_DESC.tr()}</p>
                                <div id="mfa-backup-codes" style="font-family: monospace; font-size: 13px; color: #00d9ff;"></div>
                            </div>
                            <div style="display: flex; gap: 1rem;">
                                <button id="mfa-setup-cancel" style="flex: 1; padding: 12px; border: 1px solid #333; background: transparent; color: #888; border-radius: 8px; cursor: pointer; font-size: 14px;">${StringKey.ACTION_CANCEL.tr()}</button>
                                <button id="mfa-setup-confirm" style="flex: 1; padding: 12px; border: none; background: #3B82F6; color: white; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;">${StringKey.MFA_ENABLE.tr()}</button>
                            </div>
                        </div>
                    </div>

                    <!-- MFA Disable Modal (hidden by default) -->
                    <div id="mfa-disable-modal" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 1000; align-items: center; justify-content: center;">
                        <div style="background: #1a1a1a; border-radius: 16px; padding: 2rem; max-width: 400px; width: 90%; border: 1px solid #333;">
                            <h3 style="color: #fff; margin: 0 0 1rem 0; font-size: 20px;">${StringKey.MFA_DISABLE_TITLE.tr()}</h3>
                            <p style="color: #888; font-size: 14px; margin-bottom: 1.5rem;">${StringKey.MFA_DISABLE_DESC.tr()}</p>
                            <div style="margin-bottom: 1rem;">
                                <label style="color: #fff; font-size: 14px; display: block; margin-bottom: 0.5rem;">${StringKey.MFA_VERIFICATION_CODE_LABEL.tr()}</label>
                                <input id="mfa-disable-code-input" type="text" placeholder="000000" maxlength="6" style="
                                    width: 100%;
                                    padding: 12px;
                                    border: 1px solid #333;
                                    border-radius: 8px;
                                    background: #0a0a0a;
                                    color: #fff;
                                    font-size: 18px;
                                    text-align: center;
                                    letter-spacing: 8px;
                                    box-sizing: border-box;
                                " />
                            </div>
                            <p id="mfa-disable-error" style="color: #ff4757; font-size: 13px; margin-bottom: 1rem; display: none;"></p>
                            <div style="display: flex; gap: 1rem;">
                                <button id="mfa-disable-cancel" style="flex: 1; padding: 12px; border: 1px solid #333; background: transparent; color: #888; border-radius: 8px; cursor: pointer; font-size: 14px;">${StringKey.ACTION_CANCEL.tr()}</button>
                                <button id="mfa-disable-confirm" style="flex: 1; padding: 12px; border: none; background: #ff4757; color: white; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;">${StringKey.SETTINGS_MFA_DISABLE.tr()}</button>
                            </div>
                        </div>
                    </div>

                    <!-- Phone Verification Modal -->
                    <div id="phone-verify-modal" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 1000; align-items: center; justify-content: center;">
                        <div style="background: #1a1a1a; border-radius: 16px; padding: 2rem; max-width: 400px; width: 90%; border: 1px solid #333;">
                            <h3 style="color: #fff; margin: 0 0 0.5rem 0; font-size: 20px;">${StringKey.NOTIF_SETTINGS_ENTER_CODE.tr()}</h3>
                            <p style="color: #888; font-size: 14px; margin-bottom: 1.5rem;">${StringKey.NOTIF_SETTINGS_CODE_SENT.tr()}</p>
                            <input id="phone-verify-code-input" type="text" placeholder="000000" maxlength="6" style="
                                width: 100%; padding: 12px; border: 1px solid #333; border-radius: 8px;
                                background: #0a0a0a; color: #fff; font-size: 18px; text-align: center;
                                letter-spacing: 8px; box-sizing: border-box; margin-bottom: 1rem;
                            " />
                            <p id="phone-verify-error" style="color: #ff4757; font-size: 13px; margin-bottom: 1rem; display: none;"></p>
                            <div style="display: flex; gap: 1rem;">
                                <button id="phone-verify-cancel" style="flex: 1; padding: 12px; border: 1px solid #333; background: transparent; color: #888; border-radius: 8px; cursor: pointer; font-size: 14px;">${StringKey.ACTION_CANCEL.tr()}</button>
                                <button id="phone-verify-confirm" style="flex: 1; padding: 12px; border: none; background: #00d9ff; color: #000; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;">${StringKey.NOTIF_SETTINGS_VERIFY.tr()}</button>
                            </div>
                        </div>
                    </div>

                    <!-- Delete Statement Account Confirmation Modal -->
                    <div id="delete-statement-account-modal" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.8); z-index: 1000; align-items: center; justify-content: center;">
                        <div style="background: #1a1a1a; border-radius: 16px; padding: 2rem; max-width: 400px; width: 90%; border: 1px solid #333;">
                            <h3 style="color: #ff4444; margin: 0 0 1rem 0; font-size: 20px;">${StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_TITLE.tr()}</h3>
                            <p id="delete-statement-account-message" style="color: #ccc; font-size: 14px; margin-bottom: 1rem;"></p>
                            <p style="color: #ff4444; font-size: 13px; font-weight: 500; margin-bottom: 1.5rem;">${StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_WARNING.tr()}</p>
                            <div style="display: flex; gap: 1rem;">
                                <button id="delete-statement-account-cancel" style="flex: 1; padding: 12px; border: 1px solid #333; background: transparent; color: #888; border-radius: 8px; cursor: pointer; font-size: 14px;">${StringKey.ACTION_CANCEL.tr()}</button>
                                <button id="delete-statement-account-confirm" style="flex: 1; padding: 12px; border: none; background: #ff4444; color: white; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;">${StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_CONFIRM.tr()}</button>
                            </div>
                        </div>
                    </div>

                    <!-- Unlink Confirmation Modal -->
                    <div id="unlink-confirm-modal" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: ${if (t.isDark) "rgba(0,0,0,0.8)" else "rgba(0,0,0,0.3)"}; z-index: 1000; align-items: center; justify-content: center;">
                        <div style="background: ${t.card}; border-radius: 16px; padding: 2rem; max-width: 400px; width: 90%; border: 1px solid ${t.border}; box-shadow: ${if (t.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};">
                            <h3 id="unlink-confirm-title" style="color: ${t.textPrimary}; margin: 0 0 1rem 0; font-size: 20px;"></h3>
                            <p id="unlink-confirm-message" style="color: ${t.textPrimary}; font-size: 14px; margin-bottom: 1rem;"></p>
                            <p id="unlink-confirm-accounts" style="color: ${t.textSecondary}; font-size: 13px; margin-bottom: 1.5rem;"></p>
                            <div style="display: flex; gap: 1rem;">
                                <button id="unlink-cancel" style="flex: 1; padding: 12px; border: 1px solid ${t.border}; background: transparent; color: ${t.textSecondary}; border-radius: 8px; cursor: pointer; font-size: 14px;">${StringKey.ACTION_CANCEL.tr()}</button>
                                <button id="unlink-confirm" style="flex: 1; padding: 12px; border: none; background: ${t.expenseRed}; color: white; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;">${StringKey.SETTINGS_UNLINK.tr()}</button>
                            </div>
                        </div>
                    </div>

                    <!-- Delete Account Final Confirmation Modal -->
                    <div id="delete-account-modal" style="display: none; position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: ${if (t.isDark) "rgba(0,0,0,0.8)" else "rgba(0,0,0,0.3)"}; z-index: 1000; align-items: center; justify-content: center;">
                        <div style="background: ${t.card}; border-radius: 16px; padding: 2rem; max-width: 400px; width: 90%; border: 1px solid ${t.border}; box-shadow: ${if (t.isDark) "none" else "0 20px 60px rgba(0,0,0,0.15)"};">
                            <h3 style="color: ${t.expenseRed}; margin: 0 0 1rem 0; font-size: 20px;">${StringKey.SETTINGS_DELETE_CONFIRM_TITLE.tr()}</h3>
                            <p style="color: ${t.textPrimary}; font-size: 14px; margin-bottom: 1rem;">
                                ${StringKey.SETTINGS_DELETE_CONFIRM_DESC.tr()}
                            </p>
                            <ul style="color: ${t.textSecondary}; font-size: 13px; margin-bottom: 1.5rem; padding-left: 1.5rem;">
                                <li>${StringKey.SETTINGS_DELETE_CONFIRM_LIST_ACCOUNTS.tr()}</li>
                                <li>${StringKey.SETTINGS_DELETE_CONFIRM_LIST_TRANSACTIONS.tr()}</li>
                                <li>${StringKey.SETTINGS_DELETE_CONFIRM_LIST_GOALS.tr()}</li>
                                <li>${StringKey.SETTINGS_DELETE_CONFIRM_LIST_BUDGETS.tr()}</li>
                                <li>${StringKey.SETTINGS_DELETE_CONFIRM_LIST_SUBSCRIPTION.tr()}</li>
                            </ul>
                            <p style="color: ${t.expenseRed}; font-size: 13px; font-weight: 500; margin-bottom: 1.5rem;">
                                ${StringKey.SETTINGS_DELETE_CANNOT_UNDO.tr()}
                            </p>
                            <div style="display: flex; gap: 1rem;">
                                <button id="delete-account-cancel" style="flex: 1; padding: 12px; border: 1px solid ${t.border}; background: transparent; color: ${t.textSecondary}; border-radius: 8px; cursor: pointer; font-size: 14px;">${StringKey.ACTION_CANCEL.tr()}</button>
                                <button id="delete-account-confirm" style="flex: 1; padding: 12px; border: none; background: ${t.expenseRed}; color: white; border-radius: 8px; cursor: pointer; font-size: 14px; font-weight: 500;">${StringKey.SETTINGS_DELETE_FOREVER.tr()}</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        """

        // Set up event handlers
        window.setTimeout({
            // Logo click - navigate to Landing page
            container.querySelector("#settings-logo")?.addEventListener("click", {
                navigationManager.navigateTo(Screen.Landing)
            })

            // Navigation listeners
            container.querySelector("#nav-activity")?.addEventListener("click", {
                navigationManager.switchToTab(AppTabType.TRANSACTIONS)
            })
            container.querySelector("#nav-dashboard")?.addEventListener("click", {
                navigationManager.switchToTab(AppTabType.DASHBOARD)
            })
            container.querySelector("#nav-goals")?.addEventListener("click", {
                navigationManager.switchToTab(AppTabType.GOALS)
            })
            container.querySelector("#nav-budget")?.addEventListener("click", {
                navigationManager.switchToTab(AppTabType.BUDGET_PLANNING)
            })
            // Language toggle - quick switch between languages, stay on current page
            container.querySelector("#nav-language")?.addEventListener("click", {
                LocaleManager.toggleLocale()
                window.location.reload()
            })

            // Mobile menu handlers
            if (ResponsiveUtils.isMobile()) {
                val mobileMenuOverlay = container.querySelector("#mobileMenuOverlay") as? HTMLElement
                val mobileMenuBackdrop = container.querySelector("#mobileMenuBackdrop") as? HTMLElement

                container.querySelector("#hamburgerMenu")?.addEventListener("click", {
                    mobileMenuOverlay?.style?.display = "block"
                    mobileMenuBackdrop?.style?.display = "block"
                })

                MarketingMenu.attachMobileEventListeners(
                    container = container,
                    onNavigate = ::menuNavigate,
                    onAppNavigate = ::menuAppNavigate,
                )
            }

            // Scroll listener for navbar shrink animation — only runs while this component is in the DOM
            if (!ResponsiveUtils.isMobile()) {
                window.addEventListener("scroll", { _ ->
                    if (!document.body!!.contains(container)) return@addEventListener
                    val scrollY = window.scrollY
                    val navbar = document.getElementById("settings-navbar") as? HTMLElement
                    val logo = document.getElementById("settings-logo") as? HTMLElement
                    val progress = (scrollY / 80.0).coerceIn(0.0, 1.0)
                    navbar?.style?.setProperty("padding", "${(16 - (8 * progress)).toInt()}px 32px")
                    logo?.style?.setProperty("height", "${(56 - (28 * progress)).toInt()}px")
                })
            }

            // Savings target slider event handlers
            val savingsSlider = container.querySelector("#savings-target-slider") as? HTMLInputElement
            val savingsValueDisplay = container.querySelector("#savings-target-value") as? HTMLElement

            savingsSlider?.addEventListener("input", { event ->
                val newValue = (event.target as HTMLInputElement).value.toInt()
                savingsValueDisplay?.textContent = "$newValue%"
            })

            savingsSlider?.addEventListener("change", { event ->
                val newValue = (event.target as HTMLInputElement).value.toInt()
                // Save locally
                settingsManager.setSavingsTargetPercentage(newValue.toDouble())
                // Sync to server
                GlobalScope.launch {
                    try {
                        service.setSetting("savings_target_percentage", newValue.toString())
                        console.log("Savings target synced to server: $newValue%")
                    } catch (e: Exception) {
                        console.error("Failed to sync savings target to server:", e.message)
                    }
                }
            })

            // Sync settings from server on load
            GlobalScope.launch {
                try {
                    val serverValue = service.getSetting("savings_target_percentage")
                    serverValue?.toIntOrNull()?.let { percentage ->
                        settingsManager.setSavingsTargetPercentage(percentage.toDouble())
                        savingsSlider?.value = percentage.toString()
                        savingsValueDisplay?.textContent = "$percentage%"
                        console.log("Synced savings target from server: $percentage%")
                    }
                } catch (e: Exception) {
                    console.log("Could not sync settings from server:", e.message)
                }
            }

            // Load linked banks
            val linkedBanksList = container.querySelector("#linked-banks-list") as? HTMLElement
            val unlinkModal = container.querySelector("#unlink-confirm-modal") as? HTMLElement
            var pendingUnlinkItemId: String? = null

            fun renderLinkedBanks(institutions: List<LinkedInstitution>) {
                if (institutions.isEmpty()) {
                    linkedBanksList?.innerHTML = """<p style="color: ${WebTheme.current.textSecondary}; font-size: 14px; margin: 0;">${StringKey.SETTINGS_NO_BANKS_LINKED.tr()}</p>"""
                    return
                }
                linkedBanksList?.innerHTML = institutions.joinToString("") { inst ->
                    val accountCount = inst.accounts.size
                    val needsRelink = inst.needs_relink
                    val relinkBadge = if (needsRelink) {
                        """<span style="display: inline-block; background: rgba(255,170,0,0.15); color: #ffaa00; font-size: 12px; padding: 2px 8px; border-radius: 4px; margin-left: 8px; font-weight: 500;">&#x26A0;&#xFE0F; ${StringKey.ACCOUNT_RECONNECT_NEEDED.tr()}</span>"""
                    } else ""
                    // Reconnect button — always shown so the user has a manual escape hatch
                    // when an account has stalled before our staleness heuristic triggers.
                    // When Plaid has flagged the item (needsRelink), it's the prominent orange
                    // urgent style; otherwise it's a neutral cyan outline next to Unlink.
                    val reconnectBtn = if (needsRelink) {
                        """<button class="relink-btn" data-item-id="${inst.item_id}" style="padding: 8px 16px; border: 1px solid #ffaa00; background: rgba(255,170,0,0.15); color: #ffaa00; border-radius: 6px; cursor: pointer; font-size: 13px; font-weight: 500; transition: all 0.2s;" onmouseover="this.style.backgroundColor='rgba(255,170,0,0.25)'" onmouseout="this.style.backgroundColor='rgba(255,170,0,0.15)'">${StringKey.ACCOUNT_RECONNECT_ACTION.tr()}</button>"""
                    } else {
                        """<button class="relink-btn" data-item-id="${inst.item_id}" style="padding: 8px 16px; border: 1px solid ${WebTheme.current.accent}; background: transparent; color: ${WebTheme.current.accent}; border-radius: 6px; cursor: pointer; font-size: 13px; transition: all 0.2s;" onmouseover="this.style.backgroundColor='rgba(0,217,255,0.1)'" onmouseout="this.style.backgroundColor='transparent'">${StringKey.ACCOUNT_RECONNECT_ACTION.tr()}</button>"""
                    }
                    val unlinkBtn = """<button class="unlink-btn" data-item-id="${inst.item_id}" data-name="${inst.institution_name}" data-accounts="${inst.accounts.joinToString(", ") { it.name }}" style="padding: 8px 16px; border: 1px solid #ff4444; background: transparent; color: #ff4444; border-radius: 6px; cursor: pointer; font-size: 13px; transition: all 0.2s;" onmouseover="this.style.backgroundColor='rgba(255,68,68,0.1)'" onmouseout="this.style.backgroundColor='transparent'">${StringKey.SETTINGS_UNLINK.tr()}</button>"""
                    val buttons = """<div style="display: flex; gap: 8px; align-items: center;">$reconnectBtn$unlinkBtn</div>"""
                    """
                    <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px; background: ${WebTheme.current.background}; border: 1px solid ${WebTheme.current.border}; border-radius: 8px; margin-bottom: 8px;">
                        <div>
                            <div style="color: ${WebTheme.current.textPrimary}; font-size: 15px; font-weight: 500;">${inst.institution_name}$relinkBadge</div>
                            <div style="color: ${WebTheme.current.textSecondary}; font-size: 13px;">$accountCount ${StringKey.SETTINGS_ACCOUNTS_COUNT.tr()}</div>
                        </div>
                        $buttons
                    </div>
                    """
                }
                // Wire up Reconnect buttons (always shown). Uses the shared relink flow:
                // tries update mode first, falls back to remove + fresh link if Plaid errors.
                linkedBanksList?.querySelectorAll(".relink-btn")?.let { buttons ->
                    for (i in 0 until buttons.length) {
                        val btn = buttons.item(i) as? HTMLElement ?: continue
                        btn.addEventListener("click", {
                            val itemId = btn.getAttribute("data-item-id") ?: return@addEventListener
                            startPlaidRelinkWithFallback(
                                itemId = itemId,
                                apiService = service,
                                onComplete = { _ ->
                                    GlobalScope.launch {
                                        try {
                                            val institutions = service.getLinkedInstitutions()
                                            renderLinkedBanks(institutions)
                                        } catch (e: Exception) {
                                            console.error("Failed to refresh linked banks after relink:", e.message)
                                        }
                                    }
                                },
                            )
                        })
                    }
                }
                // Wire up unlink buttons
                linkedBanksList?.querySelectorAll(".unlink-btn")?.let { buttons ->
                    for (i in 0 until buttons.length) {
                        val btn = buttons.item(i) as? HTMLElement ?: continue
                        btn.addEventListener("click", {
                            val itemId = btn.getAttribute("data-item-id") ?: return@addEventListener
                            val bankName = btn.getAttribute("data-name") ?: "this bank"
                            val accounts = btn.getAttribute("data-accounts") ?: ""
                            pendingUnlinkItemId = itemId
                            (container.querySelector("#unlink-confirm-title") as? HTMLElement)?.textContent =
                                StringKey.SETTINGS_UNLINK_CONFIRM_TITLE.tr().replace("%s", bankName)
                            (container.querySelector("#unlink-confirm-message") as? HTMLElement)?.textContent =
                                StringKey.SETTINGS_UNLINK_WARNING.tr()
                            (container.querySelector("#unlink-confirm-accounts") as? HTMLElement)?.textContent =
                                if (accounts.isNotEmpty()) "Accounts: $accounts" else ""
                            unlinkModal?.style?.display = "flex"
                        })
                    }
                }
            }

            val currentInstitutions = mutableListOf<LinkedInstitution>()

            GlobalScope.launch {
                try {
                    val institutions = service.getLinkedInstitutions()
                    currentInstitutions.clear()
                    currentInstitutions.addAll(institutions)
                    renderLinkedBanks(institutions)
                } catch (e: Exception) {
                    linkedBanksList?.innerHTML = """<p style="color: #888; font-size: 14px; margin: 0;">${StringKey.SETTINGS_LINKED_BANKS_ERROR.tr()}</p>"""
                }
            }

            container.querySelector("#unlink-cancel")?.addEventListener("click", {
                unlinkModal?.style?.display = "none"
                pendingUnlinkItemId = null
            })

            container.querySelector("#unlink-confirm")?.addEventListener("click", {
                val itemId = pendingUnlinkItemId ?: return@addEventListener
                val confirmBtn = container.querySelector("#unlink-confirm") as? HTMLElement
                confirmBtn?.textContent = "${StringKey.SETTINGS_UNLINK.tr()}..."
                confirmBtn?.style?.opacity = "0.7"

                GlobalScope.launch {
                    try {
                        service.unlinkInstitution(itemId)
                        currentInstitutions.removeAll { it.item_id == itemId }
                        renderLinkedBanks(currentInstitutions.toList())
                    } catch (e: Exception) {
                        console.error("Failed to unlink:", e.message)
                    }
                    unlinkModal?.style?.display = "none"
                    confirmBtn?.textContent = StringKey.SETTINGS_UNLINK.tr()
                    confirmBtn?.style?.opacity = "1"
                    pendingUnlinkItemId = null
                }
            })

            // ── Statement Accounts ──────────────────────────────────────
            val statementAccountsSection = container.querySelector("#statement-accounts-section") as? HTMLElement
            val statementAccountsList = container.querySelector("#statement-accounts-list") as? HTMLElement
            val deleteStatementModal = container.querySelector("#delete-statement-account-modal") as? HTMLElement
            var pendingDeleteAccountId: String? = null

            fun renderStatementAccounts(accounts: List<ApiService.ManualAccountInfo>) {
                if (accounts.isEmpty()) {
                    statementAccountsSection?.style?.display = "none"
                    return
                }
                statementAccountsSection?.style?.display = "block"
                statementAccountsList?.innerHTML = accounts.joinToString("") { account ->
                    """
                    <div style="display: flex; align-items: center; justify-content: space-between; padding: 12px; background: #0a0a0a; border-radius: 8px; margin-bottom: 8px;">
                        <div>
                            <div style="color: #fff; font-size: 15px; font-weight: 500;">${account.institutionName}</div>
                            <div style="color: #888; font-size: 13px;">${account.accountName} · ${account.transactionCount} ${StringKey.SETTINGS_STATEMENT_TRANSACTIONS_COUNT.tr()}</div>
                        </div>
                        <button class="delete-statement-btn" data-account-id="${account.accountId}" data-name="${account.institutionName} — ${account.accountName}" style="padding: 8px 16px; border: 1px solid #ff4444; background: transparent; color: #ff4444; border-radius: 6px; cursor: pointer; font-size: 13px; transition: all 0.2s;" onmouseover="this.style.backgroundColor='rgba(255,68,68,0.1)'" onmouseout="this.style.backgroundColor='transparent'">${StringKey.SETTINGS_DELETE_STATEMENT_ACCOUNT_CONFIRM.tr()}</button>
                    </div>
                    """
                }
                // Wire up delete buttons
                statementAccountsList?.querySelectorAll(".delete-statement-btn")?.let { buttons ->
                    for (i in 0 until buttons.length) {
                        val btn = buttons.item(i) as? HTMLElement ?: continue
                        btn.addEventListener("click", {
                            val accountId = btn.getAttribute("data-account-id") ?: return@addEventListener
                            val accountName = btn.getAttribute("data-name") ?: "this account"
                            pendingDeleteAccountId = accountId
                            (container.querySelector("#delete-statement-account-message") as? HTMLElement)?.textContent =
                                "Are you sure you want to delete \"$accountName\"?"
                            deleteStatementModal?.style?.display = "flex"
                        })
                    }
                }
            }

            val currentStatementAccounts = mutableListOf<ApiService.ManualAccountInfo>()

            GlobalScope.launch {
                try {
                    val accounts = service.getManualAccounts()
                    currentStatementAccounts.clear()
                    currentStatementAccounts.addAll(accounts)
                    renderStatementAccounts(accounts)
                } catch (e: Exception) {
                    console.log("Could not load statement accounts:", e.message)
                }
            }

            container.querySelector("#delete-statement-account-cancel")?.addEventListener("click", {
                deleteStatementModal?.style?.display = "none"
                pendingDeleteAccountId = null
            })

            container.querySelector("#delete-statement-account-confirm")?.addEventListener("click", {
                val accountId = pendingDeleteAccountId ?: return@addEventListener
                val confirmBtn = container.querySelector("#delete-statement-account-confirm") as? HTMLElement
                confirmBtn?.textContent = "Deleting..."
                confirmBtn?.style?.opacity = "0.7"

                GlobalScope.launch {
                    try {
                        service.deleteManualAccount(accountId)
                        currentStatementAccounts.removeAll { it.accountId == accountId }
                        renderStatementAccounts(currentStatementAccounts.toList())
                    } catch (e: Exception) {
                        console.error("Failed to delete statement account:", e.message)
                    }
                    deleteStatementModal?.style?.display = "none"
                    confirmBtn?.textContent = "Delete"
                    confirmBtn?.style?.opacity = "1"
                    pendingDeleteAccountId = null
                }
            })

            // Subscription card toggle
            val subCard = container.querySelector("#subscription-card") as? HTMLElement
            val subDetails = container.querySelector("#subscription-details") as? HTMLElement
            val subChevron = container.querySelector("#subscription-chevron") as? HTMLElement
            subCard?.addEventListener("click", { e ->
                // Don't toggle if clicking a button or link inside
                val target = e.target as? HTMLElement
                if (target?.tagName == "BUTTON" || target?.tagName == "A") return@addEventListener
                val isOpen = subDetails?.style?.display != "none"
                subDetails?.style?.display = if (isOpen) "none" else "block"
                subChevron?.style?.transform = if (isOpen) "rotate(0deg)" else "rotate(180deg)"
            })

            // Subscribe CTA button
            container.querySelector("#settings-subscribe-cta")?.addEventListener("click", { e ->
                e.stopPropagation()
                navigationManager.navigateTo(Screen.Subscribe())
            })

            // Manage Subscription — opens Stripe Customer Portal for Stripe
            // subscribers; for users without a Stripe customer (iOS/Apple IAP,
            // Android/Play Store IAP, or admin-set test accounts), expands an
            // inline panel offering the three platform-specific paths instead
            // of dropping them onto the paywall (which is wrong UX for users
            // who already have an active subscription).
            container.querySelector("#manage-subscription-btn")?.addEventListener("click", { e ->
                e.stopPropagation()
                val channelsPanel = container.querySelector("#manage-subscription-channels") as? HTMLElement
                GlobalScope.launch {
                    try {
                        val returnUrl = window.location.href
                        val portalUrl = apiService.getStripePortalUrl(returnUrl)
                        if (portalUrl != null) {
                            window.location.href = portalUrl
                        } else {
                            // No Stripe customer → show the channel picker inline.
                            channelsPanel?.style?.display = "block"
                        }
                    } catch (e: Exception) {
                        console.error("Manage subscription failed: ${e.message}")
                        channelsPanel?.style?.display = "block"
                    }
                }
            })

            // Delete Account functionality (multi-step)
            val deleteAccountModal = container.querySelector("#delete-account-modal") as? HTMLElement
            val softConfirm = container.querySelector("#delete-account-soft-confirm") as? HTMLElement

            // Step 1: Subtle link shows soft confirmation
            container.querySelector("#deleteAccountLink")?.addEventListener("click", { e ->
                e.preventDefault()
                e.stopPropagation()
                softConfirm?.style?.display = "block"
                // Scroll it into view
                softConfirm?.asDynamic()?.scrollIntoView(js("{ behavior: 'smooth', block: 'nearest' }"))
            })

            // "Contact Support" in soft confirm
            container.querySelector("#delete-account-contact-support")?.addEventListener("click", {
                navigationManager.navigateTo(Screen.Contact)
            })

            // Step 2: "Yes, I want to delete" opens the final modal
            container.querySelector("#delete-account-proceed")?.addEventListener("click", { e ->
                e.preventDefault()
                softConfirm?.style?.display = "none"
                deleteAccountModal?.style?.display = "flex"
            })

            container.querySelector("#delete-account-cancel")?.addEventListener("click", {
                deleteAccountModal?.style?.display = "none"
            })
            container.querySelector("#delete-account-confirm")?.addEventListener("click", {
                val confirmBtn = container.querySelector("#delete-account-confirm") as? HTMLElement
                confirmBtn?.textContent = StringKey.SETTINGS_DELETING.tr()
                confirmBtn?.style?.opacity = "0.7"
                GlobalScope.launch {
                    try {
                        service.deleteAccount()
                        deleteAccountModal?.style?.display = "none"
                        service.clearSession()
                        navigationManager.logout()
                    } catch (e: Exception) {
                        confirmBtn?.textContent = StringKey.SETTINGS_DELETE_FOREVER.tr()
                        confirmBtn?.style?.opacity = "1"
                        console.error("Failed to delete account: ${e.message}")
                    }
                }
            })

            // MFA Settings functionality
            val mfaSection = container.querySelector("#mfa-settings-section") as? HTMLElement
            val mfaStatusText = container.querySelector("#mfa-status-text") as? HTMLElement
            val mfaStatusBadge = container.querySelector("#mfa-status-badge") as? HTMLElement
            val enableMfaBtn = container.querySelector("#enable-mfa-btn") as? HTMLElement
            val disableMfaBtn = container.querySelector("#disable-mfa-btn") as? HTMLElement
            val mfaSetupModal = container.querySelector("#mfa-setup-modal") as? HTMLElement
            val mfaDisableModal = container.querySelector("#mfa-disable-modal") as? HTMLElement

            // Store MFA setup data for later use
            var mfaBackupCodes: List<String> = emptyList()

            // Load MFA status
            GlobalScope.launch {
                try {
                    val status = service.getMfaStatus()
                    if (status.mfaAvailable) {
                        mfaSection?.style?.display = "block"
                        if (status.mfaEnabled) {
                            mfaStatusText?.textContent = StringKey.MFA_ENABLED_DESC.tr()
                            mfaStatusBadge?.textContent = StringKey.MFA_ENABLED.tr()
                            mfaStatusBadge?.style?.cssText = "padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; background: #22C55E; color: white;"
                            enableMfaBtn?.style?.display = "none"
                            disableMfaBtn?.style?.display = "block"
                        } else {
                            mfaStatusText?.textContent = StringKey.MFA_DISABLED_DESC.tr()
                            mfaStatusBadge?.textContent = StringKey.MFA_DISABLED.tr()
                            mfaStatusBadge?.style?.cssText = "padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; background: #666; color: white;"
                            enableMfaBtn?.style?.display = "block"
                            disableMfaBtn?.style?.display = "none"
                        }
                    } else {
                        // MFA not available (not in production mode)
                        mfaSection?.style?.display = "none"
                    }
                } catch (e: Exception) {
                    console.error("Failed to load MFA status: ${e.message}")
                    mfaSection?.style?.display = "none"
                }
            }

            // Enable MFA button click
            enableMfaBtn?.addEventListener("click", {
                GlobalScope.launch {
                    try {
                        val setupData = service.setupMfa()
                        mfaBackupCodes = setupData.backupCodes

                        // Show QR code
                        val qrCode = container.querySelector("#mfa-qr-code") as? HTMLElement
                        val secretCode = container.querySelector("#mfa-secret-code") as? HTMLElement
                        val backupCodesDiv = container.querySelector("#mfa-backup-codes") as? HTMLElement

                        // Generate QR code using a simple approach with Google Charts API
                        val encodedUri = js("encodeURIComponent")(setupData.otpAuthUri)
                        qrCode?.setAttribute("src", "https://chart.googleapis.com/chart?cht=qr&chs=200x200&chl=$encodedUri")
                        secretCode?.textContent = setupData.secret
                        backupCodesDiv?.innerHTML = setupData.backupCodes.joinToString("<br>")

                        // Show modal
                        mfaSetupModal?.style?.display = "flex"
                    } catch (e: Exception) {
                        console.error("Failed to setup MFA: ${e.message}")
                        showAppAlert("Failed to set up MFA: ${e.message}")
                    }
                }
            })

            // Setup modal - Cancel button
            container.querySelector("#mfa-setup-cancel")?.addEventListener("click", {
                mfaSetupModal?.style?.display = "none"
                // Clear input
                (container.querySelector("#mfa-setup-code-input") as? HTMLInputElement)?.value = ""
                (container.querySelector("#mfa-setup-error") as? HTMLElement)?.style?.display = "none"
            })

            // Setup modal - Confirm button
            container.querySelector("#mfa-setup-confirm")?.addEventListener("click", {
                val codeInput = container.querySelector("#mfa-setup-code-input") as? HTMLInputElement
                val errorElement = container.querySelector("#mfa-setup-error") as? HTMLElement
                val code = codeInput?.value ?: ""

                if (code.length != 6) {
                    errorElement?.textContent = StringKey.MFA_ENTER_CODE_ERROR.tr()
                    errorElement?.style?.display = "block"
                    return@addEventListener
                }

                GlobalScope.launch {
                    try {
                        service.verifyMfaSetup(code, mfaBackupCodes)
                        mfaSetupModal?.style?.display = "none"

                        // Update UI to show MFA is enabled
                        mfaStatusText?.textContent = StringKey.MFA_ENABLED_DESC.tr()
                        mfaStatusBadge?.textContent = StringKey.MFA_ENABLED.tr()
                        mfaStatusBadge?.style?.cssText = "padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; background: #22C55E; color: white;"
                        enableMfaBtn?.style?.display = "none"
                        disableMfaBtn?.style?.display = "block"

                        // Clear input
                        codeInput?.value = ""
                        errorElement?.style?.display = "none"
                    } catch (e: Exception) {
                        errorElement?.textContent = e.message ?: "Invalid code. Please try again."
                        errorElement?.style?.display = "block"
                    }
                }
            })

            // Disable MFA button click
            disableMfaBtn?.addEventListener("click", {
                mfaDisableModal?.style?.display = "flex"
            })

            // Disable modal - Cancel button
            container.querySelector("#mfa-disable-cancel")?.addEventListener("click", {
                mfaDisableModal?.style?.display = "none"
                // Clear input
                (container.querySelector("#mfa-disable-code-input") as? HTMLInputElement)?.value = ""
                (container.querySelector("#mfa-disable-error") as? HTMLElement)?.style?.display = "none"
            })

            // Disable modal - Confirm button
            container.querySelector("#mfa-disable-confirm")?.addEventListener("click", {
                val codeInput = container.querySelector("#mfa-disable-code-input") as? HTMLInputElement
                val errorElement = container.querySelector("#mfa-disable-error") as? HTMLElement
                val code = codeInput?.value ?: ""

                if (code.length != 6) {
                    errorElement?.textContent = StringKey.MFA_ENTER_CODE_ERROR.tr()
                    errorElement?.style?.display = "block"
                    return@addEventListener
                }

                GlobalScope.launch {
                    try {
                        service.disableMfa(code)
                        mfaDisableModal?.style?.display = "none"

                        // Update UI to show MFA is disabled
                        mfaStatusText?.textContent = StringKey.MFA_DISABLED_DESC.tr()
                        mfaStatusBadge?.textContent = StringKey.MFA_DISABLED.tr()
                        mfaStatusBadge?.style?.cssText = "padding: 4px 12px; border-radius: 12px; font-size: 12px; font-weight: 500; background: #666; color: white;"
                        enableMfaBtn?.style?.display = "block"
                        disableMfaBtn?.style?.display = "none"

                        // Clear input
                        codeInput?.value = ""
                        errorElement?.style?.display = "none"
                    } catch (e: Exception) {
                        errorElement?.textContent = e.message ?: "Invalid code. Please try again."
                        errorElement?.style?.display = "block"
                    }
                }
            })

            // Notification section — non-Pro: click to upgrade
            if (!isProTier) {
                container.querySelector("#notif-settings-section")?.addEventListener("click", {
                    navigationManager.navigateTo(Screen.Subscribe(proOnly = true))
                })
            }

            // Notification Preferences functionality
            if (isProTier) {
                val notifToggle = container.querySelector("#notif-toggle") as? HTMLInputElement
                val notifToggleTrack = container.querySelector("#notif-toggle-track") as? HTMLElement
                val notifToggleThumb = container.querySelector("#notif-toggle-thumb") as? HTMLElement
                val notifOptions = container.querySelector("#notif-options") as? HTMLElement
                val channelEmailBtn = container.querySelector("#channel-email-btn") as? HTMLElement
                val channelSmsBtn = container.querySelector("#channel-sms-btn") as? HTMLElement
                val channelBothBtn = container.querySelector("#channel-both-btn") as? HTMLElement
                val channelEmailCheck = container.querySelector("#channel-email-check") as? HTMLElement
                val channelSmsCheck = container.querySelector("#channel-sms-check") as? HTMLElement
                val channelBothCheck = container.querySelector("#channel-both-check") as? HTMLElement
                val phoneSection = container.querySelector("#notif-phone-section") as? HTMLElement
                val phoneInput = container.querySelector("#notif-phone-input") as? HTMLInputElement
                val verifyBtn = container.querySelector("#notif-verify-btn") as? HTMLElement
                val phoneStatus = container.querySelector("#notif-phone-status") as? HTMLElement
                val consentCheckbox = container.querySelector("#notif-consent-checkbox") as? HTMLInputElement
                val phoneVerifyModal = container.querySelector("#phone-verify-modal") as? HTMLElement
                val phoneVerifyCodeInput = container.querySelector("#phone-verify-code-input") as? HTMLInputElement
                val phoneVerifyError = container.querySelector("#phone-verify-error") as? HTMLElement
                val phoneVerifyCancel = container.querySelector("#phone-verify-cancel") as? HTMLElement
                val phoneVerifyConfirm = container.querySelector("#phone-verify-confirm") as? HTMLElement

                val testFireBtn = container.querySelector("#notif-test-fire-btn") as? HTMLElement
                val testFireResult = container.querySelector("#notif-test-fire-result") as? HTMLElement

                // State
                var notifEnabled = false
                var notifChannel = "email" // "email", "sms", "both"
                // E.164-normalised phone we last successfully verified. Editing the
                // input flips the computed `phoneVerified` off automatically; reverting
                // to the same number flips it back on without a server roundtrip
                // (matches iOS/Android).
                var verifiedPhone: String? = null
                var phoneNumber = ""
                var smsConsent = false

                fun normalizeToE164(raw: String): String {
                    val stripped = raw.filter { it.isDigit() || it == '+' }
                    if (stripped.isEmpty()) return ""
                    if (stripped.startsWith("+")) return stripped
                    return when {
                        stripped.length == 10 -> "+1$stripped"
                        stripped.length == 11 && stripped.startsWith("1") -> "+$stripped"
                        else -> "+$stripped"
                    }
                }

                fun phoneIsVerified(): Boolean {
                    val v = verifiedPhone ?: return false
                    val current = normalizeToE164(phoneNumber)
                    return v.isNotEmpty() && current == v
                }

                fun updateToggleVisual(enabled: Boolean) {
                    if (enabled) {
                        notifToggleTrack?.style?.background = "#00d9ff"
                        notifToggleThumb?.style?.transform = "translateX(20px)"
                    } else {
                        notifToggleTrack?.style?.background = "#333"
                        notifToggleThumb?.style?.transform = "translateX(0)"
                    }
                    notifOptions?.style?.display = if (enabled) "block" else "none"
                }

                fun updateChannelChecks() {
                    channelEmailCheck?.textContent = if (notifChannel == "email") "\u2713" else ""
                    channelSmsCheck?.textContent = if (notifChannel == "sms") "\u2713" else ""
                    channelBothCheck?.textContent = if (notifChannel == "both") "\u2713" else ""
                    val needsPhone = notifChannel == "sms" || notifChannel == "both"
                    phoneSection?.style?.display = if (needsPhone) "block" else "none"
                }

                fun updatePhoneStatus() {
                    // The badge takes the SAME SLOT as the Verify button (right of the
                    // phone input). Exactly one is visible \u2014 never both, never below.
                    if (phoneIsVerified()) {
                        verifyBtn?.style?.display = "none"
                        phoneStatus?.style?.display = "inline-flex"
                        phoneStatus?.textContent = "\u2713 ${StringKey.NOTIF_SETTINGS_PHONE_VERIFIED.tr()}"
                    } else {
                        phoneStatus?.style?.display = "none"
                        verifyBtn?.style?.display = "inline-block"
                        verifyBtn?.textContent = StringKey.NOTIF_SETTINGS_VERIFY.tr()
                        verifyBtn?.style?.background = "#00d9ff"
                        verifyBtn?.style?.cursor = "pointer"
                    }
                }

                fun saveNotifPreferences() {
                    val prefsJson = """{"enabled":$notifEnabled,"channel":"$notifChannel","phone_number":"$phoneNumber","phone_verified":${phoneIsVerified()},"sms_consent":$smsConsent}"""
                    GlobalScope.launch {
                        try {
                            service.setSetting("notification_preferences", prefsJson)
                            console.log("Notification preferences saved")
                        } catch (e: Exception) {
                            console.error("Failed to save notification preferences:", e.message)
                        }
                    }
                }

                // Load existing preferences
                GlobalScope.launch {
                    try {
                        val raw = service.getSetting("notification_preferences")
                        if (raw != null) {
                            val prefs = JSON.parse<dynamic>(raw)
                            notifEnabled = (prefs.enabled as? Boolean) ?: false
                            notifChannel = (prefs.channel as? String) ?: "email"
                            phoneNumber = (prefs.phone_number as? String) ?: ""
                            // Translate server's phone_verified flag into our verifiedPhone
                            // snapshot so phoneIsVerified() evaluates correctly on first render.
                            val serverVerified = (prefs.phone_verified as? Boolean) ?: false
                            verifiedPhone =
                                if (serverVerified && phoneNumber.isNotBlank()) {
                                    normalizeToE164(phoneNumber)
                                } else {
                                    null
                                }
                            smsConsent = (prefs.sms_consent as? Boolean) ?: false

                            notifToggle?.checked = notifEnabled
                            updateToggleVisual(notifEnabled)
                            updateChannelChecks()
                            if (phoneNumber.isNotBlank()) {
                                phoneInput?.value = phoneNumber
                            }
                            consentCheckbox?.checked = smsConsent
                            updatePhoneStatus()
                        }
                    } catch (e: Exception) {
                        console.log("Could not load notification preferences:", e.message)
                    }
                }

                // Toggle handler
                notifToggle?.addEventListener("change", {
                    notifEnabled = notifToggle.checked
                    updateToggleVisual(notifEnabled)
                    saveNotifPreferences()
                })

                // Channel selection handlers
                fun selectChannel(channel: String) {
                    notifChannel = channel
                    updateChannelChecks()
                    updatePhoneStatus()
                    saveNotifPreferences()
                }
                channelEmailBtn?.addEventListener("click", { selectChannel("email") })
                channelSmsBtn?.addEventListener("click", { selectChannel("sms") })
                channelBothBtn?.addEventListener("click", { selectChannel("both") })

                // SMS consent handler
                consentCheckbox?.addEventListener("change", {
                    smsConsent = consentCheckbox.checked
                    saveNotifPreferences()
                })

                // Phone field edit handler — phoneNumber tracks the input directly,
                // and updatePhoneStatus() re-evaluates the verified state by comparing
                // the normalised current value to verifiedPhone. Edit to a different
                // number → badge disappears. Revert → badge restores. No server save
                // on transient edits; the verified snapshot is preserved client-side.
                phoneInput?.addEventListener("input", {
                    phoneNumber = phoneInput.value
                    updatePhoneStatus()
                })

                // Verify button - open modal
                verifyBtn?.addEventListener("click", {
                    if (phoneIsVerified()) return@addEventListener
                    if (!smsConsent) return@addEventListener
                    val phone = normalizeToE164(phoneInput?.value?.trim() ?: "")
                    if (phone.isBlank()) return@addEventListener
                    phoneInput?.value = phone
                    phoneNumber = phone

                    verifyBtn.textContent = "..."
                    GlobalScope.launch {
                        try {
                            service.verifyPhone(phone)
                            phoneVerifyModal?.style?.display = "flex"
                            phoneVerifyCodeInput?.value = ""
                            phoneVerifyError?.style?.display = "none"
                            verifyBtn.textContent = StringKey.NOTIF_SETTINGS_VERIFY.tr()
                        } catch (e: Exception) {
                            console.error("Failed to send verification code:", e.message)
                            verifyBtn.textContent = StringKey.NOTIF_SETTINGS_VERIFY.tr()
                        }
                    }
                })

                // Phone verify modal - cancel
                phoneVerifyCancel?.addEventListener("click", {
                    phoneVerifyModal?.style?.display = "none"
                })

                // Phone verify modal - confirm
                phoneVerifyConfirm?.addEventListener("click", {
                    val code = phoneVerifyCodeInput?.value?.trim() ?: ""
                    if (code.length != 6) return@addEventListener
                    val phone = phoneInput?.value?.trim() ?: ""

                    phoneVerifyConfirm.textContent = "..."
                    GlobalScope.launch {
                        try {
                            val result = service.confirmPhone(phone, code)
                            if (result.status == "verified") {
                                verifiedPhone = normalizeToE164(phone)
                                phoneNumber = phone
                                updatePhoneStatus()
                                saveNotifPreferences()
                                phoneVerifyModal?.style?.display = "none"
                            } else {
                                phoneVerifyError?.textContent = result.message ?: "Invalid code"
                                phoneVerifyError?.style?.display = "block"
                            }
                        } catch (e: Exception) {
                            phoneVerifyError?.textContent = e.message ?: "Verification failed"
                            phoneVerifyError?.style?.display = "block"
                        }
                        phoneVerifyConfirm.textContent = StringKey.NOTIF_SETTINGS_VERIFY.tr()
                    }
                })

                // Close modal on backdrop click
                phoneVerifyModal?.addEventListener("click", { event ->
                    if (event.target == phoneVerifyModal) {
                        phoneVerifyModal.style.display = "none"
                    }
                })

                // "Send test notification" — parity with the iOS Settings button.
                // Calls /api/notifications/test-fire with reset=3 so the user can
                // self-confirm their email/SMS channel works without waiting an hour.
                testFireBtn?.addEventListener("click", {
                    testFireBtn.textContent = i18n.StringKey.NOTIF_TEST_SENDING.tr()
                    (testFireBtn as? HTMLButtonElement)?.disabled = true
                    testFireResult?.style?.display = "none"
                    GlobalScope.launch {
                        try {
                            val result = service.fireTestNotification(reset = 3)
                            testFireResult?.style?.display = "block"
                            if (result.sent) {
                                testFireResult?.style?.color = "#22C55E"
                                testFireResult?.textContent = i18n.StringKey.NOTIF_TEST_SENT.tr()
                            } else {
                                testFireResult?.style?.color = "#EF4444"
                                testFireResult?.textContent = result.hint ?: i18n.StringKey.NOTIF_TEST_NOTHING_FIRED.tr()
                            }
                        } catch (e: Exception) {
                            testFireResult?.style?.display = "block"
                            testFireResult?.style?.color = "#EF4444"
                            testFireResult?.textContent = e.message ?: i18n.StringKey.NOTIF_TEST_FAILED.tr()
                        }
                        testFireBtn.innerHTML = "<span style=\"font-size: 14px;\">✈</span> <span>${i18n.StringKey.NOTIF_TEST_SEND.tr()}</span>"
                        (testFireBtn as? HTMLButtonElement)?.disabled = false
                    }
                })
            }

            // Dashboard mode selector
            val stdBtn = container.querySelector("#dashboard-mode-standard") as? HTMLElement
            val gridBtn = container.querySelector("#dashboard-mode-grid") as? HTMLElement
            fun updateDashboardModeButtons() {
                val isStd = dashboardMode == "standard"
                stdBtn?.style?.apply {
                    borderColor = if (isStd) "rgba(0,217,255,0.4)" else "#333"
                    background = if (isStd) "rgba(0,217,255,0.1)" else "transparent"
                    color = if (isStd) "${t.accent}" else "${t.textSecondary}"
                }
                gridBtn?.style?.apply {
                    borderColor = if (!isStd && isProTier) "rgba(0,217,255,0.4)" else "#333"
                    background = if (!isStd && isProTier) "rgba(0,217,255,0.1)" else "transparent"
                    color = if (!isStd && isProTier) "${t.accent}" else "${t.textSecondary}"
                }
            }
            stdBtn?.addEventListener("click", {
                dashboardMode = "standard"
                localStorage.setItem("dashboard_mode", "standard")
                updateDashboardModeButtons()
            })
            gridBtn?.addEventListener("click", {
                if (isProTier) {
                    dashboardMode = "grid"
                    localStorage.setItem("dashboard_mode", "grid")
                    updateDashboardModeButtons()
                } else {
                    navigationManager.navigateTo(Screen.Subscribe(proOnly = true))
                }
            })

            // Theme preference buttons
            container.querySelector("#theme-system-btn")?.addEventListener("click", {
                WebTheme.applyPreference("system")
                GlobalScope.launch { try { service.setSetting("theme_preference", "system") } catch (_: Exception) {} }
                window.location.reload()
            })
            container.querySelector("#theme-dark-btn")?.addEventListener("click", {
                WebTheme.applyPreference("dark")
                GlobalScope.launch { try { service.setSetting("theme_preference", "dark") } catch (_: Exception) {} }
                window.location.reload()
            })
            container.querySelector("#theme-light-btn")?.addEventListener("click", {
                WebTheme.applyPreference("light")
                GlobalScope.launch { try { service.setSetting("theme_preference", "light") } catch (_: Exception) {} }
                window.location.reload()
            })

            // Language Settings functionality
            container.querySelector("#lang-en-btn")?.addEventListener("click", {
                if (LocaleManager.getCurrentLocale() != AppLocale.ENGLISH) {
                    LocaleManager.setLocale(AppLocale.ENGLISH)
                    window.location.reload()
                }
            })

            container.querySelector("#lang-fr-btn")?.addEventListener("click", {
                if (LocaleManager.getCurrentLocale() != AppLocale.FRENCH) {
                    LocaleManager.setLocale(AppLocale.FRENCH)
                    window.location.reload()
                }
            })

        }, 0)

        return container
    }
    private fun renderSavingsGoals(screen: Screen.SavingsGoals, container: HTMLElement) {
        container.innerHTML = ""
        val component = WebSavingsGoalsComponent(navigationManager, screen.openCreateDialog, isProTier, showProTag)
        container.appendChild(component.render())
    }

    private fun renderRecommendations(screen: Screen.Recommendations, container: HTMLElement) {
        container.innerHTML = ""
        val component = WebRecommendationsComponent(navigationManager)
        container.appendChild(component.render())
    }

    private fun renderWiz3RCardDetail(screen: Screen.Wiz3RCardDetail, container: HTMLElement) {
        container.innerHTML = ""
        val card = navigationManager.getSelectedWiz3rCard()
        val component = WebWiz3RCardDetailComponent(
            navigationManager = navigationManager,
            card = card,
            anchor = screen.anchor,
            openChat = screen.openChat,
            onBack = {
                navigationManager.navigateBack()
                rerender()
            },
        )
        container.appendChild(component.render())
    }
}
